import type {
  GenerationBasketSelection,
  GenerationCaseBasis,
  GenerationSourceReceipt,
} from './quality-generation-v4.js';
/**
 * Wire types for the user-facing Quality surface (cti-quality-service via
 * `/quality/*`). Top-level responses are snake_case and request bodies camelCase;
 * embedded versioned snapshots preserve their persisted camelCase schema. The
 * platform JWT identifies the user — the backend scopes every list to the
 * user's granted projects / own runs, so the client never filters by owner.
 *
 * Shapes mirror the service contract exactly; do not add fields the server
 * does not send.
 */

export type Platform = 'web' | 'mobile';
export type WebNetworkCaptureMode = 'errors-only' | 'full-har';
export type WebNetworkCaptureOverride = WebNetworkCaptureMode | null;

export type EnvironmentMode = 'default' | 'named' | 'none';

export type ExternalTestSource = 'testrail' | 'zephyr' | 'azure' | 'xray';

export type SourceRef =
  | {
      provider: 'testrail';
      projectId: string;
      sourceLinkId: string;
      coordinates:
        | {
            kind: 'run';
            externalProjectId: string;
            runId: string;
            suiteId?: string | null;
          }
        | {
            kind: 'suite';
            externalProjectId: string;
            suiteId: string;
          };
    }
  | {
      provider: 'zephyr';
      projectId: string;
      sourceLinkId: string;
      coordinates:
        | {
            kind: 'cycle';
            externalProjectId: string;
            cycleKey: string;
          }
        | {
            kind: 'folder';
            externalProjectId: string;
            folderId: string;
          };
    }
  | {
      provider: 'azure';
      projectId: string;
      sourceLinkId: string;
      coordinates: {
        kind: 'suite';
        externalProjectId: string;
        planId: string;
        suiteId: string;
      };
    }
  | {
      provider: 'xray';
      projectId: string;
      sourceLinkId: string;
      coordinates: {
        kind: 'testPlan' | 'testSet';
        externalProjectId: string;
        containerId: string;
      };
    }
  | {
      /** A suite stored in Quality itself. It has no source link, and its external project is the
       *  Quality project. */
      provider: 'own-tms';
      projectId: string;
      coordinates: {
        kind: 'suite';
        externalProjectId: string;
        suiteId: string;
      };
    };

export type QualityMcpOperationName =
  | 'quality.regression_create'
  | 'quality.regression_update'
  | 'quality.generate_combined'
  | 'quality.exploratory'
  | 'quality.run'
  | 'quality.preflight_run'
  | 'quality.restart_failed'
  | 'quality.rerun_all'
  | 'quality.rerun_current'
  | 'quality.get_launch_options'
  | 'quality.get_suite_launch_settings'
  | 'quality.set_suite_launch_settings'
  | 'quality.get_capabilities'
  | 'quality.cancel_run';

export interface QualityMcpOperationCapability {
  operation_version: number;
  input_schema_version: number;
  output_schema_version: number;
  required_fields: readonly string[];
  input_fields: readonly string[];
  strict_input: boolean;
}

export interface QualityCapabilitiesResponse {
  service: 'cti-quality-service';
  semantic_version: string;
  schema_version: number;
  transport: {
    authentication: 'verified_bearer_per_request';
    reconnect: 'repeat_reads_only';
    direct_delivery: 'pull';
    chat_subscription: boolean;
  };
  operations: Partial<Record<string, QualityMcpOperationCapability>>;
  ordinary_execution: {
    version: 1;
    sources: Array<
      'testrail' | 'zephyr' | 'azure' | 'xray' | 'own-tms' | 'inline' | 'mock'
    >;
    source_ref_schema_version: number;
    environment_modes: EnvironmentMode[];
    target_platforms: Platform[];
    relaunch_target_platforms: { omitted: 'inherit'; null: 'auto' };
    explicit_false_preserved: Array<
      'human_interaction_enabled' | 'takeover_enabled'
    >;
    legacy_platform: {
      accepted: true;
      deprecated: true;
      effective_field: 'target_platforms';
    };
  };
}

export type LaunchOptionStatus =
  'available' | 'unavailable' | 'requires_runtime_validation';

export interface LaunchOptionReason {
  code: string;
  message: string;
}

export interface LaunchOptionsResponse {
  version: 1;
  project: { id: string; name: string; workspace_id: string | null };
  permissions: {
    can_view: true;
    can_run: boolean;
    can_manage_settings: boolean;
  };
  environments: {
    default_name: string | null;
    default_status: 'available' | 'unavailable';
    options: Array<{
      name: string;
      is_default: boolean;
      credential_ref: string | null;
      status: 'available' | 'unavailable';
      reasons: LaunchOptionReason[];
    }>;
    reasons: LaunchOptionReason[];
  };
  sources: {
    options: Array<{
      provider:
        | 'testrail'
        | 'zephyr'
        | 'azure'
        | 'xray'
        | 'own-tms'
        | 'inline'
        | 'mock';
      source_link_id: string | null;
      external_project_id: string | null;
      status: LaunchOptionStatus;
      reasons: LaunchOptionReason[];
    }>;
    failures: Array<{
      source_link_id: string | null;
      reasons: LaunchOptionReason[];
    }>;
  };
  target_platforms: Array<{
    platform: Platform;
    configured: boolean;
    status: LaunchOptionStatus;
    reasons: LaunchOptionReason[];
  }>;
  additional_components: { api: boolean };
  credentials: {
    status: 'available' | 'unavailable';
    options: Array<{
      ref: string;
      label: string;
      kind: CredentialKind;
    }>;
    reasons: LaunchOptionReason[];
  };
  effective_settings: {
    environment_mode: 'default';
    environment_name: string | null;
    target_platforms: null;
    human_interaction_enabled: boolean;
    takeover_enabled: boolean;
    parallelism: null;
    /** The project's case execution mode; absent on a service that predates the setting. */
    execution_mode?: RunExecutionMode;
    model: { runner: string; model: string; modelVariant?: string } | null;
    model_source:
      'project_policy' | 'global_default' | 'invalid_project_policy';
  };
  input_options: {
    environment_modes: EnvironmentMode[];
    target_platforms: { values: Platform[]; auto: null };
    parallelism: { minimum: number; maximum: number; default: null };
    execution_modes?: RunExecutionMode[];
    direct_model_overrides: string[];
  };
  launch_readiness: {
    status: 'ready' | 'blocked';
    reasons: LaunchOptionReason[];
    revalidation_required: true;
  };
}

/** Terminal + in-flight states for a whole run. */
export type RunStatus =
  | 'running'
  | 'awaiting_review'
  // A case hit an unanswered request_user_input() past its hold window. Not terminal — the run
  // resumes via restart-failed (a fresh run re-prompts) or cancel. See InputRequiredPanel.
  | 'awaiting_input'
  | 'reporting'
  | 'passed'
  | 'failed'
  /** Terminal: no case failed, but not every case was checked (a case ended `timeout` or `broken`).
   *  Distinct from `failed` on purpose — `failed` means the application under test misbehaved,
   *  `incomplete` makes no claim about the product at all. Decided server-side by `runVerdict()`. */
  | 'incomplete'
  | 'cancelled';

/** Query-time outcome for the explicit Workspace knowledge source on a generation run. */
export type WorkspaceKnowledgeRunStatus =
  | 'pending'
  | 'ready'
  | 'empty'
  | 'unavailable'
  | 'forbidden'
  | 'malformed'
  | 'oversized'
  | 'persistence_failure'
  | (string & {});

/** Additive run-level state for Workspace knowledge; no credentials or raw retrieval payloads. */
export interface WorkspaceKnowledgeRunState {
  enabled: boolean;
  status: WorkspaceKnowledgeRunStatus;
  message?: string;
}

/**
 * Why the control-plane safety-net reaper auto-finalized a run (orthogonal to
 * `status` — a reaped run still carries a normal terminal status). `null` on the
 * overwhelming majority of runs, which finalize normally via the worker callback.
 * 'never_started' = reaped while every case was still queued (never executed);
 * 'stalled' = a case started, then the run went silent and was force-finalized.
 */
export type ReapedReason = 'never_started' | 'stalled';

/** Per-case result states. */
// 'timeout' = started but ran out of session/case time (distinct from 'broken' = started then crashed).
export type ResultStatus =
  | 'queued'
  | 'running'
  | 'passed'
  | 'failed'
  | 'broken'
  | 'timeout'
  | 'cancelled';

export type ExecutionEstimateReason =
  | 'missing_usage'
  | 'invalid_usage'
  | 'missing_rate_snapshot'
  | 'expired_rates'
  | 'catalog_unavailable'
  | 'unverified_vendor'
  | 'model_mismatch'
  | 'unknown_tier'
  | 'unknown_cache_usage'
  | 'unknown_cache_write_rate'
  | 'unsupported_tariff_shape'
  | 'unverified_discount'
  | 'capture_failed'
  | 'sequence_gap'
  | 'missing_child'
  | 'missing_start'
  | 'unsealed_parent'
  | 'unsealed_invocation'
  | 'conflicting_evidence'
  | 'response_attribution_conflict'
  | 'observation_overflow'
  | 'retention_expired'
  | 'spool_quota'
  | 'spool_io'
  | 'clock_skew'
  | 'unsupported_schema';

/** Additive executor-request estimate. Recorded cost fields keep their existing meaning. */
export interface ExecutionEstimateV1 {
  schema_version: 1;
  currency: 'USD';
  scope: 'quality_executor_requests';
  amount_usd: string | null;
  amount_kind: 'complete' | 'subtotal' | 'unknown' | 'proven_zero';
  method_counts: { catalog_calculation: number; provider_reported: 0 };
  known_request_count: number;
  priced_request_count: number;
  unknown_request_count: number;
  total_request_count: number | null;
  request_coverage: { priced: number; total: number } | null;
  expected_invocation_count: number | null;
  observed_invocation_count: number;
  request_population_complete: boolean;
  invocation_population_complete: boolean;
  required_parent_count: number;
  sealed_parent_count: number;
  stale_rate_request_count: number;
  reason_counts: Partial<Record<ExecutionEstimateReason, number>>;
  bases: {
    provider: 'deepinfra';
    model: string;
    tier: 'standard' | 'priority' | 'flex' | 'unknown';
    method: 'catalog_calculation';
    snapshot_ids: string[];
    amount_usd: string;
    priced_request_count: number;
    fetched_at_from: string;
    fetched_at_through: string;
  }[];
  bases_truncated: boolean;
  as_of: string | null;
  revision: string;
  final: boolean;
}

/** Canonical version-1 execution lifecycle. Case verdicts are intentionally a separate axis. */
export type ExecutionStatus =
  | 'running'
  | 'awaiting_review'
  | 'awaiting_input'
  | 'reporting'
  | 'completed'
  | 'partially_completed'
  | 'not_completed'
  | 'cancelled';

/** What a repeated run does after an iteration whose outcome is not passed (MEDA-1288). */
export type IterationFailurePolicy = 'stop' | 'continue';

/** Canonical terminal case outcomes. Queued and running are represented by {@link CaseState}. */
export type CaseOutcome =
  | 'unresolved'
  | 'passed'
  | 'defect_found'
  | 'case_unclear'
  | 'blocked_on_input'
  | 'qa_error'
  | 'infra_error'
  | 'cancelled';

export type CaseState = 'queued' | 'running' | CaseOutcome;

/**
 * What a case shows: its state, `assessing` while the AI judge has not ruled on it yet, or
 * `not_run` for a repeated run's iteration the stop policy skipped (MEDA-1288; stored as `cancelled`).
 */
export type PresentedCaseStatus = CaseState | 'assessing' | 'not_run';

/**
 * Run counts as shown to people: unconfirmed results count as `unresolved`, unjudged ones as
 * `assessing`. `not_run` is absent on a service that predates repeated runs.
 */
export type PresentedResults = Record<
  Exclude<CaseOutcome, 'unresolved'> | 'unresolved' | 'assessing',
  number
> & { not_run?: number };

/** Why a case is still `assessing`. `ready_by` is null, and `estimated` true, while no judge window is open. */
export interface CaseAssessment {
  state: 'assessing';
  ready_by: string | null;
  estimated: boolean;
}

export const CASE_OUTCOMES = [
  'unresolved',
  'passed',
  'defect_found',
  'case_unclear',
  'blocked_on_input',
  'qa_error',
  'infra_error',
  'cancelled',
] as const satisfies readonly CaseOutcome[];

export type OutcomeResults = Record<
  Exclude<CaseOutcome, 'unresolved'>,
  number
> & {
  unresolved?: number;
};

export interface HistoricalCaseProvenance {
  mapping_version: number;
  original_status: string;
  attempt_source: 'test_attempts' | 'initial_zero_counters' | null;
  reasons: string[];
}

export interface HistoricalRunProvenance {
  mapping_version: number;
  original_status: string;
  delivery_suppressed: true;
  reasons?: string[];
}

export type TriageReason =
  | 'low_confidence'
  | 'judge_timeout'
  | 'judge_error'
  | 'judge_missing'
  | 'judge_invalid'
  | 'judge_indeterminate'
  | 'historical_ambiguous'
  | 'cleanup_unconfirmed'
  | (string & {});

export interface OutcomeClassification {
  source:
    | 'preliminary'
    | 'deterministic'
    | 'judge'
    | 'fallback'
    | 'history'
    | 'manual';
  preliminary_outcome: CaseOutcome | null;
  reason_code: string | null;
  rationale: string | null;
  judge: {
    request_id: string;
    outcome: 'defect_found' | 'case_unclear' | 'qa_error';
    confidence: number;
    rationale: string;
    model: string;
    rubric_version: string;
  } | null;
  correction_id: string | null;
  manual?: {
    actor_id: string;
    actor_name: string | null;
    comment: string;
    applied_at: string;
  };
}

export type DeliveryRequestState =
  | 'pending'
  | 'sending'
  | 'acknowledged'
  | 'failed'
  | 'unknown'
  | 'conflict'
  | 'skipped';

export interface DeliverySummary {
  state: 'pending' | 'partial' | 'delivered' | 'problem';
  counts: Record<DeliveryRequestState, number>;
}

/** Pipeline stage states (fetch → fan-out → run → aggregate → report). */
export type StageStatus =
  'pending' | 'running' | 'passed' | 'failed' | 'cancelled';

/** The pipeline stages a run moves through, in order. `preflight` is the two-stage execution gate
 *  (cred-check + BrowserStack-slot reservation) between fetch-tests and execute. Cast from the API,
 *  not validated — the stage chip renders any name, so an unknown stage still shows. */
export type StageName =
  'setup' | 'fetch-tests' | 'preflight' | 'execute' | 'report' | (string & {});

export interface RunSummary {
  total: number;
  passed: number;
  failed: number;
  pending: number;
  // Additive (PR-4 / report-correctness): the live summary now reports broken + timeout as their own
  // buckets (no longer lumped into failed). Optional for back-compat with older servers / responses.
  broken?: number;
  timeout?: number;
  /** Version-1 summary. Legacy scalar buckets remain present only on null-protocol responses. */
  results?: OutcomeResults;
  unconfirmed_qa_errors?: number;
}

export interface ExploratoryInputSourceSnapshot {
  kind: 'jira_story' | 'tms_case';
  ref: string;
  connector: 'jira' | 'testrail' | 'zephyr' | 'xray' | 'azure' | (string & {});
  loadStatus: 'loaded' | 'unresolved';
  /** Planned routing, decided at launch time — not proof of what the run actually did. */
  usage: 'browsing_prompt' | 'post_processing' | 'not_used';
  /**
   * What actually happened, merged in from the worker's terminal callback once the run completes.
   * 'post_processing' means the post-session pass returned an assessed result for this source;
   * 'post_processing_attempted' means the pass ran but returned nothing assessed for it (timeout,
   * crash, `not_assessed`, or no amendment for the case). Absent on a run created before this
   * field existed, or one that has not reached its terminal callback yet — the UI must show that
   * as "unknown", never read `usage` as if it were proof.
   */
  actualUsage?: 'post_processing' | 'post_processing_attempted' | 'not_used';
  failure?: { code: string; message: string };
}

export interface ExploratoryLaunchSnapshot {
  source: {
    type: 'environment' | 'customUrl' | 'projectCapability';
    environmentName?: string;
    customUrl?: string;
    capabilityLabel?: string;
    appId?: string;
  };
  resolvedUrl: string;
  effectiveCredential: {
    kind: 'environment' | 'override' | 'anonymous' | (string & {});
    credentialId?: string;
  };
  effectiveHitl: {
    hitlState?: 'active' | 'paused' | 'disabled';
  };
  workerSnapshot: { platform?: Platform };
  input?: {
    focus?: string;
    maxMinutes?: number;
    documents?: Array<{ filename: string; kind: string }>;
    sources?: ExploratoryInputSourceSnapshot[];
  };
}

/**
 * Where a not-yet-started run waits (MEDA-798). Null once a worker has picked the run up. The
 * estimate fields stay null until the service ships a calibrated start window; render them only
 * when present.
 */
export interface QueueWait {
  state:
    | 'waiting_for_worker'
    | 'waiting_for_browserstack'
    | 'waiting_for_project_slot'
    | 'waiting_for_device'
    | 'queue_paused'
    | 'no_workers';
  waited_seconds: number;
  queue_position: number | null;
  queue_length: number | null;
  estimated_wait_seconds: { low: number; high: number } | null;
  confidence: 'low' | 'medium' | 'high' | null;
  no_estimate_reason:
    'not_enough_history' | 'queue_paused' | 'no_workers' | 'human_input' | null;
}

/** One source file the agent will not have for a case. `reason` is one of `case_attachment_limit`,
 *  `run_size_limit`, `file_rejected`, `download_failed` or `embedded_image_not_attached`; a newer
 *  server may send another. `filename` is null for a pasted image whose link carries no name. */
export interface AttachmentIssue {
  case_id: string;
  filename: string | null;
  reason: string;
}

export interface Run {
  workspace_id?: string | null;
  publication_warnings?: {
    provider: 'testrail' | 'azure' | 'xray';
    code: string;
    case_ids?: string[];
    message: string;
  }[];
  id: string;
  web_network_capture_mode?: WebNetworkCaptureMode | null;
  environment_name?: string | null;
  /** Variable values this run was launched with (MEDA-865); absent on an older service. */
  variable_overrides?: Record<string, string> | null;
  /** Per-project run number ("Run #N"); null on legacy rows + runs with no project. */
  run_number: number | null;
  suite: string | null;
  platform: Platform;
  /** 'execution' (default) runs predefined cases; 'exploration'/'doc_synthesis' are the legacy
   *  single-source generators; 'generation' is the unified combined-synthesis flow with a review
   *  loop (proposes cases, awaits approval, then writes them to the configured test management
   *  system — TestRail, Zephyr, or Azure DevOps); 'exploratory' is a standalone
   *  browse that produces a report (execution log + potential bugs + screenshots) and no test cases. */
  run_kind?:
    | 'execution'
    | 'validation'
    | 'exploration'
    | 'doc_synthesis'
    | 'generation'
    | 'exploratory';
  source: string | null;
  /** Canonical source identity the run was launched from; null for inline and mock runs, absent on
   *  servers that predate it. */
  source_ref?: SourceRef | null;
  project_id: string | null;
  project_name: string | null;
  testrail_run_id: string | null;
  testrail_suite_id: string | null;
  /** Zephyr-sourced runs only: the cycle launched from (write-back target) / the read-only folder.
   *  Optional for back-compat with servers predating the Zephyr source. */
  zephyr_cycle_key?: string | null;
  zephyr_folder_id?: string | null;
  /** Azure-sourced runs only: the ADO project + Test Plan + Test Suite launched from. Optional for
   *  back-compat with servers predating the Azure source. */
  azure_project?: string | null;
  azure_plan_id?: string | null;
  azure_suite_id?: string | null;
  /** Xray-sourced runs only: the Jira project key + the Test Plan or Test Set launched from (exactly
   *  one of the two ids is set), plus the Test Execution Xray creates for write-back. Optional for
   *  back-compat with servers predating the Xray source. */
  xray_project_key?: string | null;
  xray_test_plan_id?: string | null;
  xray_test_set_id?: string | null;
  xray_test_execution_key?: string | null;
  /** Generation runs only, Xray output target: the Jira project key + the test-repository folder or
   *  Test Set the approved cases were written to. Optional for back-compat. */
  xray_output_project_key?: string | null;
  xray_output_folder_path?: string | null;
  xray_output_test_set_id?: string | null;
  xray_output_test_plan_id?: string | null;
  xray_output_new_test_set_summary?: string | null;
  xray_output_new_test_plan_summary?: string | null;
  /** Generation runs only, Quality output target: the stored suite the approved cases are saved
   *  in. Absent on servers that predate it. */
  local_output_suite_id?: string | null;
  /** Generation runs only: where approved cases are written. Exactly one set is populated — TestRail
   *  (project + section), Zephyr (project key + folder), or Azure DevOps (project + plan + suite).
   *  Used to name the output target in the review/approve UI. Optional for back-compat. */
  testrail_project?: string | null;
  testrail_section_id?: number | null;
  zephyr_output_project_key?: string | null;
  zephyr_output_folder_id?: string | null;
  azure_output_project?: string | null;
  azure_output_plan_id?: string | null;
  azure_output_suite_id?: string | null;
  /** Present only for explicit null-protocol history. Canonical runs omit this legacy alias. */
  status?: RunStatus;
  /** Null identifies retained legacy history. Version 1 omits the legacy status aliases on the wire. */
  outcome_protocol_version?: 1 | null;
  execution_status?: ExecutionStatus;
  results?: OutcomeResults;
  unconfirmed_qa_errors?: number;
  /** Counts as shown to people; only on enrolled execution runs, absent on an older service. */
  presented_results?: PresentedResults;
  history?: HistoricalRunProvenance;
  history_migration?: {
    mapping_version: number;
    state:
      | 'pending'
      | 'applied'
      | 'quarantined'
      | 'deferred_live'
      | 'preserved_changed';
    reason: string | null;
  };
  status_caption?: string | null;
  completion_warning?: 'cleanup_unconfirmed' | null;
  report_revision?: number | null;
  delivery_summary?: DeliverySummary;
  /** The status a client should render as the badge — `run.badge_status ?? run.status`. Equals
   *  `status` for every run finalized under the current rule; differs only on a historical row the
   *  old folded rule stored as `failed` when nothing had actually failed (repaired to `incomplete`
   *  for display). Optional only for back-compat with a server predating the field — a current
   *  server always sends it. cti-quality-service's `runVerdict()`/`badgeStatus()` is the sole
   *  authority for this value; the client must never re-derive it from counters or markers. */
  badge_status?: RunStatus;
  /** Non-null only when the safety-net reaper auto-finalized the run; drives the
   *  secondary "Never ran" / "Auto-closed" badge. Orthogonal to `status`. */
  reaped_reason?: ReapedReason | null;
  /** Who cancelled the run (JWT `sub`) and their display name, snapshotted the same way as
   *  `created_by`/`created_by_name`. Both null or absent on every run predating this field, and on a
   *  run that was never cancelled or cancelled by the worker/system path with no viewer — optional
   *  because an older server omits the keys entirely rather than sending them as `null`. "When" is
   *  the existing `finished_at` below — there is no separate cancel timestamp. Only
   *  `cancelled_by_name` is ever rendered (as the status badge's tooltip, never a second visible
   *  badge) — matching how launcher attribution elsewhere in this SPA uses `created_by_name` and
   *  never falls back to the raw id; `cancelled_by` is carried for parity with the service payload,
   *  not for display. */
  cancelled_by?: string | null;
  cancelled_by_name?: string | null;
  /** The model that drives the browser for this run's cases; null on rows that predate it, absent on
   *  an older service. */
  model?: string | null;
  total: number;
  concurrency: number;
  /** How the run dispatched its cases; absent on a service that predates the setting. */
  execution_mode?: RunExecutionMode;
  report_key: string | null;
  /** Identity id and snapshotted display name of whoever launched the run (null on legacy rows). */
  created_by: string | null;
  created_by_name: string | null;
  created_at: string;
  started_at: string | null;
  finished_at: string | null;
  /** Present while the run waits for pickup; absent or null on older servers and once started. */
  queue_wait?: QueueWait | null;
  /** Null while active (the default); set to when the run was archived, hiding it from the
   *  default runs board without ever deleting it. Optional because an older server (mid rolling
   *  deploy) omits the key entirely rather than sending it as null. */
  archived_at?: string | null;
  /** Bug-hunt mode only: potential bugs observed during the live-app browse (display-only). */
  findings?: Finding[] | null;
  /** Any explore run: deterministic execution log of the browse (display-only). */
  execution_steps?: ExecutionStep[] | null;
  /** Standalone exploratory launch inputs and source-resolution outcomes. */
  exploratory_launch_snapshot?: ExploratoryLaunchSnapshot | null;
  /** Mobile only: the BrowserStack session recording URL (display-only). Web runs surface their
   *  video as an uploaded .webm artifact instead (see the case's artifact_keys). */
  video_url?: string | null;
  /** Generation runs only, on the approve response: titles of approved cases skipped because they
   *  already exist in the target section/folder (existing-tests de-dup). Absent on older servers. */
  skipped_existing?: string[];
  /** Components the run's cases actually touch, chosen by the preflight pre-scan (a superset of the
   *  scalar `platform`). Absent on single-component runs and servers predating the pre-scan. */
  components?: string[];
  /** Components the project is configured for ({web,mobile} ∩ capabilities; mobile needs a
   *  BrowserStack account). The pre-scan picks the run's components from this set. */
  available_components?: string[];
  /** The explicit platform restriction used for this run; null means automatic project selection. */
  requested_platforms?: Platform[] | null;
  /** Scarce device-session retention policy chosen for a multi-component run ('hold' keeps the
   *  session open until the run ends, 'per-block' releases it after each platform). Null/absent on
   *  single-component runs. */
  retention?: 'hold' | 'per-block' | null;
  /** When the two-stage execution gate cleared this run's preflight; null while it still awaits the
   *  gate. Absent on servers predating the pre-scan. */
  preflight_at?: string | null;
  /** Why the preflight gate failed (a credential the pre-scan needs isn't configured), or the
   *  pre-scan reported no component in the available set so the run fell back to the full set. Null
   *  when the pre-scan agreed with the available set. */
  preflight_error?: string | null;
  /** Why the run reached a terminal state, when one reason explains it ('preflight_failed' when the
   *  preflight gate itself stopped the run). Null on a run that ended without one, and absent on
   *  servers predating the field — which is why `PreflightPanel` treats only the explicit
   *  'preflight_failed' as a failure and everything else as an advisory. */
  terminal_reason?: string | null;
  /** True when the source-attachment restage (Zephyr/TestRail file fixtures) skipped or left
   *  unresolved at least one attachment. False on every run that never hit that path or hit it
   *  cleanly, and on servers predating this field. */
  attachments_incomplete?: boolean;
  /** The files behind `attachments_incomplete`: one entry per case, file and reason. Null or absent
   *  on runs that recorded no list, including every run from before the field existed. */
  attachment_issues?: AttachmentIssue[] | null;
  /** Whether this run currently holds an unsettled, non-expired help request (a device takeover
   *  mid-drag, or a typed input nobody answered yet) — drives the "Needs help" badge in the runs
   *  list (spec F1). `status` itself never changes for this: a run needing help is still
   *  'running'. Optional with a falsy default so a service response predating the field (e.g.
   *  during a single-container recreate) renders no badge instead of crashing. */
  needs_help?: boolean;
  /** Effective HITL policy snapshotted for this run. */
  human_interaction?: {
    input_enabled: boolean;
    takeover_enabled: boolean;
    source: string;
  } | null;
  /** Explicit Workspace knowledge source outcome; null/absent on disabled and legacy runs. */
  workspace_knowledge?: WorkspaceKnowledgeRunState | null;
  /** Omitted by services that predate executor accounting. */
  execution_estimate?: ExecutionEstimateV1;
  summary?: RunSummary;
}

/** The output fields that identify where a generation run writes its approved cases. */
type RunOutputTarget = Pick<
  Run,
  | 'testrail_project'
  | 'testrail_section_id'
  | 'zephyr_output_project_key'
  | 'zephyr_output_folder_id'
  | 'azure_output_plan_id'
  | 'xray_output_project_key'
  | 'xray_output_folder_path'
  | 'xray_output_test_set_id'
  | 'xray_output_test_plan_id'
  | 'local_output_suite_id'
>;

/**
 * A generation run's output test-management system, from whichever output-field set the server
 * populated. Returns null when none is set (e.g. an execution run, or a server predating the field).
 */
export function runOutputSource(
  run: RunOutputTarget,
): 'testrail' | 'zephyr' | 'azure' | 'xray' | 'own-tms' | null {
  if (run.local_output_suite_id) return 'own-tms';
  if (run.azure_output_plan_id) return 'azure';
  if (run.zephyr_output_project_key || run.zephyr_output_folder_id)
    return 'zephyr';
  if (
    run.xray_output_project_key ||
    run.xray_output_folder_path ||
    run.xray_output_test_set_id ||
    run.xray_output_test_plan_id
  )
    return 'xray';
  if (run.testrail_project || run.testrail_section_id != null)
    return 'testrail';
  return null;
}

/**
 * Human name of a generation run's output test-management system for review/approve copy. Falls back
 * to a neutral phrase so the wording is never wrong when the target can't be determined.
 */
export function runOutputTargetLabel(run: RunOutputTarget): string {
  switch (runOutputSource(run)) {
    case 'azure':
      return 'Azure DevOps';
    case 'zephyr':
      return 'Zephyr Scale';
    case 'xray':
      return 'Xray';
    case 'testrail':
      return 'TestRail';
    case 'own-tms':
      return 'Quality';
    default:
      return 'the test management system';
  }
}

/** The concrete kind of work a run performs (the non-null `run_kind` axis). */
export type RunKind = NonNullable<Run['run_kind']>;
/** The coarse "family" a kind rolls up into — drives the list badge + filter. */
export type RunFamily = 'test' | 'generate' | 'explore';

/** Human-readable label per kind. The name prefix uses this; absent kind → 'Run' (see runKindLabel). */
const KIND_LABEL: Record<RunKind, string> = {
  execution: 'Execution',
  validation: 'Validation',
  exploration: 'Exploration',
  doc_synthesis: 'Doc Synthesis',
  generation: 'Generation',
  exploratory: 'Exploratory',
};

/** Kind → family. `exploration` synthesizes cases (→ generate); `exploratory` is a standalone
 *  browse with no cases (→ explore). See docs/run-naming-and-filters.md. */
const KIND_FAMILY: Record<RunKind, RunFamily> = {
  execution: 'test',
  validation: 'test',
  exploration: 'generate',
  doc_synthesis: 'generate',
  generation: 'generate',
  exploratory: 'explore',
};

/** Family of a run; undefined kind ⇒ 'test' (legacy runs are executions). An unknown kind from a
 *  newer server also falls back to 'test' rather than crashing icon/badge consumers. */
export function runFamily(kind?: Run['run_kind']): RunFamily {
  return (kind ? KIND_FAMILY[kind] : undefined) ?? 'test';
}

/** Human label for a kind; 'Run' when absent (legacy / old-server back-compat) or unknown (a newer
 *  server emitted a kind this build doesn't know yet). */
export function runKindLabel(kind?: Run['run_kind']): string {
  return (kind ? KIND_LABEL[kind] : undefined) ?? 'Run';
}

/** The run's display name: a typed prefix (`Execution #42`, `Generation #42`, …) + its per-project
 *  number, or a short-id label for legacy / project-less runs. When `run_kind` is absent the prefix
 *  falls back to `Run`, so legacy / old-server runs read exactly as before ("Run #42"). The suite
 *  name is secondary info, shown separately — not the title. */
export function runName(
  run: Pick<Run, 'run_number' | 'id' | 'run_kind'>,
): string {
  const prefix = runKindLabel(run.run_kind);
  return run.run_number != null
    ? `${prefix} #${run.run_number}`
    : `${prefix} ${run.id.slice(0, 8)}`;
}

export interface RunStage {
  name: StageName;
  status: StageStatus;
  position: number;
  started_at: string | null;
  finished_at: string | null;
}

export interface Step {
  name?: string;
  status?: string;
  [k: string]: unknown;
}

/** Caching status of one case step: whether the recorded script replays it, and the reason code
 * when it doesn't and the compiler recorded one. */
export interface ReplayStepStatus {
  step_index: number;
  step_text: string | null;
  cached: boolean;
  reason: string | null;
  /** Human-readable labels of the actions a cached step replays (click, type, navigate, …). */
  actions: string[];
}

/**
 * Auto-Recorded Test Replay detail for one case in a run (MEDA-416): how the case ran this time and
 * whether a deterministic replay script is on file for its current definition. Mirrors the service
 * `replay` object; `non_cacheable_reason` is a raw code the UI maps to human text.
 */
export interface CaseReplay {
  mode: ExecutionMode | null;
  cached: boolean;
  // API-cast: keep the known states for autocomplete but tolerate a newer server's unknown value.
  cacheable: 'active' | 'superseded' | 'non_cacheable' | 'none' | (string & {});
  non_cacheable_reason: string | null;
  step_count: number | null;
  healed_step: number | null;
  /** Every case step classified cached/not-cached; absent on older servers. */
  steps?: ReplayStepStatus[];
}

/** One value a case saved from an API response: the value, where it came from, and the step. */
export interface CapturedVariable {
  value?: unknown;
  source?: string;
  phase?: string | null;
  index?: number | null;
}

/**
 * One rubric criterion the answer judge scored (MEDA-943). The service always sends all five, in the
 * order of the known names; `(string & {})` keeps a newer server's name readable.
 */
export interface AnswerJudgementCriterion {
  name:
    | 'answers_question'
    | 'follows_format'
    | 'stays_grounded'
    | 'clarity'
    | 'language_and_tone'
    | (string & {});
  /** Whole number from 1 to 5. */
  score: number;
  reason: string;
}

/** One item of a case's answer checklist and the judge's result for it. `result` and `reason` are
 *  null when the case was not scored. */
export interface AnswerJudgementChecklistItem {
  number: number;
  text: string;
  critical: boolean;
  result: 'met' | 'not_met' | 'unclear' | (string & {}) | null;
  reason: string | null;
}

/** One answer judge skill a verdict ran (MEDA-943): its id and the sha-256 hex digest of its text. */
export interface AnswerJudgeSkillRef {
  id: string;
  digest: string;
}

/**
 * The answer judge's verdict on one case (MEDA-943), `answer_judgement` on a result. Named apart from
 * `classification.judge`, which is the outcome classifier. The service computes the three summary
 * fields, never the judge.
 */
export interface AnswerJudgement {
  status: 'scored' | 'not_scored' | (string & {});
  not_scored_reason:
    | 'no_answer'
    | 'judge_error'
    | 'invalid_verdict'
    | 'judge_timeout'
    | 'no_capture'
    | (string & {})
    | null;
  /** What the judge read: the answer captured from the chat, the case's `answer_text`, or nothing. */
  evidence_kind: 'capture' | 'fallback' | 'none' | (string & {});
  judge_model: string | null;
  /** The run's `AGENT_MODELS` variable, as text; null when the run set none. */
  agent_models: string | null;
  /** Mean of the five criterion scores, two decimals; null when not scored. */
  rubric_average: number | null;
  /** Met items ÷ items not unclear × 100; null without a checklist or a clear item. */
  checklist_percent: number | null;
  /** True when a critical checklist item was not met. */
  critical_miss: boolean;
  /** Milliseconds from sending the judged message to the end of its reply; null when not measured,
   *  absent on an older service. */
  answer_ms?: number | null;
  /** What the judge itself cost for this case, in USD; null when not reported, absent on an older
   *  service. */
  judge_cost_usd?: number | null;
  criteria: AnswerJudgementCriterion[];
  checklist: AnswerJudgementChecklistItem[];
  /** The judge skills this verdict ran, each with the sha-256 digest of its text. `facts` is null when
   *  the case had no checklist. Absent on an older service; cast, not validated. */
  skills?: {
    quality: AnswerJudgeSkillRef;
    facts: AnswerJudgeSkillRef | null;
  } | null;
  created_at: string;
}

/** Answer quality across a run's judged cases (MEDA-943). The two averages are means of the
 *  per-case values over the scored cases. */
export interface AnswerJudgementSummary {
  judged_cases: number;
  scored_cases: number;
  rubric_average: number | null;
  checklist_percent: number | null;
  critical_miss_cases: number;
  /** Median and 90th percentile answer time over the judged cases with one; absent on an older
   *  service. */
  answer_ms_median?: number | null;
  answer_ms_p90?: number | null;
  /** What the judge itself cost for the run, in USD, apart from the tester's recorded report cost;
   *  null when no judged case reported a cost, absent on an older service. */
  judge_cost_usd?: number | null;
}

export interface TestResult {
  case_id: string;
  /** Result-row UUID, distinct from the source case id. Present for protocol version 1. */
  case_execution_id?: string;
  attempt?: number | null;
  history?: HistoricalCaseProvenance;
  outcome?: CaseState;
  outcome_revision?: number;
  outcome_finalized_at?: string | null;
  needs_triage?: boolean;
  triage_reason?: TriageReason | null;
  classification?: OutcomeClassification | null;
  /** The status to show; only on cases of an enrolled execution run, absent on an older service. */
  display_status?: PresentedCaseStatus;
  display_label?: string | null;
  assessment?: CaseAssessment | null;
  title: string | null;
  spec: unknown;
  status: ResultStatus;
  steps: Step[];
  artifact_keys: string[];
  duration_ms: number | string | null;
  cost_usd: number | string | null;
  /** Separate from cost_usd; omitted by services that predate executor accounting. */
  execution_estimate?: ExecutionEstimateV1;
  /** False when a partial/aborted case could not reliably record cost; absent means legacy/current behavior. */
  cost_recorded?: boolean;
  /** Stable enum code for why the case terminated without a normal verdict (reaper, scheduling
   *  exhaustion, cancel, dead worker shard, …); null when it passed or a genuine failure carries its
   *  detail elsewhere. Mapped to an English sentence in quality-presentation `caseReasonText`. */
  reason_code?: string | null;
  /** Plain values the case saved from API responses (MEDA-865), keyed by name. Never secrets.
   *  Absent on an older service; the shape is cast, not validated, so read it defensively. */
  captured_variables?: Record<string, CapturedVariable> | null;
  updated_at: string;
  /** Auto-Recorded Test Replay detail; null on older runs or callers that didn't resolve it. */
  replay?: CaseReplay | null;
  /** The udid the shard's session actually ran on, confirmed against `driver.caps` by the worker.
   *  Null on older results and on non-farm (BrowserStack/local) runs. */
  device_udid?: string | null;
  /** Whether this case ran the app sync step ('cold') or the guard skipped it ('warm'). Null when
   *  the case didn't touch a mobile lane. */
  sync_state?: 'cold' | 'warm' | null;
  /** How long the sync step took, in milliseconds. Null when the case didn't run one. */
  sync_duration_ms?: number | null;
  /** How many of this case's steps the wave-2 verdict rule downgraded from `failed` to
   *  `passed`/`deviation: true` (`wave2-contract.md` #2/#3). Absent/0 on a case the rule never
   *  touched and on a server predating wave 2 — a run with 0 deviations must look exactly as it
   *  did before this field existed. Typed as `number | string | null` because the response is
   *  type-cast, not validated (`(await res.json()) as T`), and any non-number here must go
   *  through `deviationCount()` rather than a truthy check or arithmetic. */
  deviation_count?: number | string | null;
  /** Durable warnings produced while composing a source-linked run. They are stored with the run so
   *  reports and chat cards can explain source drift after the launch dialog is gone. */
  adaptation_warnings?: AdaptationWarning[];
  /** The latest answer judge verdict on this case (MEDA-943); null when it was not judged, absent on
   *  an older service. Cast, not validated, so read it through `readAnswerJudgement`. */
  answer_judgement?: AnswerJudgement | null;
}

export interface GenerationResult {
  version: number | null;
  retained_case_count: number;
  available_draft_count: number;
  can_publish: boolean;
  state:
    | 'cancelled'
    | 'running'
    | 'ready'
    | 'saved'
    | 'publishing'
    | 'publication_unresolved'
    | 'failed'
    | 'empty';
  severity: 'info' | 'warning' | 'error';
  message: string | null;
}

export interface RunDetail {
  generation_result?: GenerationResult;
  run: Omit<Run, 'summary'>;
  stages: RunStage[];
  results: TestResult[];
  summary: RunSummary;
  /** Answer quality across the run (MEDA-943); null when no case was judged, absent on an older
   *  service. */
  answer_judgement_summary?: AnswerJudgementSummary | null;
  /** Some read projections expose the run estimate beside the run object. */
  execution_estimate?: ExecutionEstimateV1;
  outcome_history?: OutcomeHistoryPage;
  delivery?: DeliveryPage;
}

export interface OutcomeHistoryItem {
  request_id: string;
  case_id: string;
  case_execution_id: string;
  old_outcome: CaseOutcome;
  new_outcome: CaseOutcome;
  old_needs_triage: boolean;
  new_needs_triage: boolean;
  prior_classification: OutcomeClassification | null;
  classification: OutcomeClassification | null;
  comment: string;
  actor: { id: string; display_name: string | null };
  applied_at: string;
  outcome_revision: number;
  report_revision: number;
}

export interface OutcomeHistoryPage {
  items: OutcomeHistoryItem[];
  next_cursor: string | null;
}

export interface DeliveryReceipt {
  id: string;
  status: string;
  verified_at: string;
}

export interface DeliveryItem {
  delivery_id: string;
  origin: 'initial' | 'correction';
  case_id: string | null;
  outcome_revision: number | null;
  report_revision: number;
  channel: string;
  destination_label: string;
  state: DeliveryRequestState;
  reason_code: string | null;
  next_attempt_at: string | null;
  acknowledged_at: string | null;
  external_receipt: DeliveryReceipt | null;
  retained_external_receipt: DeliveryReceipt | null;
}

export interface DeliveryPage {
  summary: DeliverySummary;
  items: DeliveryItem[];
  next_cursor: string | null;
}

export interface OutcomeCorrectionBody {
  outcome: Exclude<CaseOutcome, 'unresolved'>;
  comment: string;
  expectedRevision: number;
  requestId: string;
}

export interface OutcomeCorrectionReceipt {
  request_id: string;
  case_id: string;
  case_execution_id: string;
  outcome: CaseOutcome;
  outcome_revision: number;
  report_revision: number;
  applied_at: string;
}

/** The per-platform capability config (mobile carries the device/app coordinates; web is config-less).
 *  `udid` is used by self-hosted Appium farms to pin a run to one device; BrowserStack ignores it. */
export interface Capability {
  device?: string;
  os?: string;
  appId?: string;
  appUrl?: string;
  /** Optional self-hosted farm device identifier (UDID). */
  udid?: string;
}

/** What a project's capabilities map may be keyed by. `api` is a COMPONENT, not a platform: it opens
 *  no session and a run is never launched on it, so it stays out of {@link Platform} while sharing
 *  the same operator-facing map. It rides alongside web/mobile and cannot be a project's only
 *  capability — the server rejects that (MEDA-363). */
export type CapabilityKey = Platform | 'api';

export type Capabilities = Partial<Record<CapabilityKey, Capability>>;

/** One device (real or simulator) a project may lease on the device farm (project-settings registry entry).
 *  `enabled` is opt-out: absent means the device is usable. */
export interface DeviceRegistryEntry {
  udid: string;
  label?: string;
  enabled?: boolean;
}

/** One row of `GET /projects/:id/device-leases` (operator lease panel). The list is scoped by the
 *  device farm, not by project — two projects on the farm contend for the same
 *  device, so a lease held by another project is still returned, with
 *  `belongsToThisProject: false`. That field is not optional: the panel renders those rows read-only,
 *  with no Break control. `inFlight` is whether the owning run has any case actually executing right
 *  now — `false` is the stuck-lane signal the panel warns on. */
export interface DeviceLease {
  id?: string;
  canForceRelease?: boolean;
  udid: string;
  label?: string;
  ownerRunId: string;
  ownerRunName?: string;
  leasedAt: string;
  leaseUntil: string;
  inFlight: boolean;
  belongsToThisProject: boolean;
}

/** Postman-style environment for a project's launch targets (Block C). `name` is dynamic and unique
 *  per project (case-insensitive) — NOT a fixed dev/stage/prod enum. `baseUrl` + `vars` are plain
 *  data substituted into case text at run time (`{{BASE_URL}}` / `{{key}}`); `secretVarKeys` is a
 *  presence-only ledger — the values live in Vault server-side and are never returned to the client.
 *  `credentialId` optionally binds a "Browser sign-in" credential (default none; the UI may suggest a
 *  match by URL but never auto-binds). */
/** Uploaded file fixture metadata (bytes live in the artifact store; never inlined here). */
export interface ProjectFileVar {
  filename: string;
  contentType: string;
  sizeBytes: number;
  storageKey: string;
}

export interface EnvironmentConfig {
  name: string;
  baseUrl?: string;
  vars?: Record<string, string>;
  secretVarKeys?: string[];
  credentialId?: string;
  apiAuthMode?: 'inherit' | 'none' | 'salesforce_jwt' | 'workspace';
  apiCredentialId?: string;
  /** Origins an `api` step may call on this environment (MEDA-363). Empty or absent means the API
   *  tool refuses every call — fail closed, never default-permissive. */
  apiAllowedOrigins?: string[];
  /** Per-environment file fixtures; override project `sharedFileVars` on the same key. */
  fileVars?: Record<string, ProjectFileVar>;
}

/** Write-only body shared by environment create/update (Block C). `vars` is a full replace when
 *  provided (send `{}` to clear all plain vars). `secretVars` is write-only per key: a key mapped to
 *  '' clears that Vault entry, a key mapped to a non-empty value (re)writes it, an omitted key is left
 *  untouched. `credentialId: ''` explicitly clears a previously-bound credential on update (the server
 *  treats a defined-but-falsy value as unbind, distinct from an omitted key which leaves the binding
 *  untouched); a non-empty value binds/switches it. See the client-side note in ProjectSettingsPanel
 *  for why create mode never sends `''`. */
export interface EnvironmentWriteBody {
  baseUrl?: string;
  vars?: Record<string, string>;
  secretVars?: Record<string, string>;
  credentialId?: string;
  apiAuthMode?: 'inherit' | 'none' | 'salesforce_jwt' | 'workspace';
  apiCredentialId?: string;
  /** Full replace when provided; `[]` clears the list. Omitted leaves it untouched. */
  apiAllowedOrigins?: string[];
}

export type QualityRunner = 'claude-code' | 'opencode' | 'pi';

/** A runner + its bare model id. Claude ids are the platform slugs (`claude-sonnet-5`); OpenCode and
 *  Pi ids are the bare HuggingFace ids DeepInfra serves (`moonshotai/Kimi-K2.5`) — never the
 *  `deepinfra:`-prefixed admin storage form. */
export interface RunnerModelChoice {
  runner: QualityRunner;
  model: string;
}

/** The original project policy: one default plus optional per-operation overrides. */
export interface RunnerPolicyV1 {
  version: 1;
  default: RunnerModelChoice;
  operations?: Partial<{
    runs: RunnerModelChoice;
    explore: RunnerModelChoice;
    generate: RunnerModelChoice;
  }>;
}

/** A service-job model choice: service jobs call a provider directly, with no runner. */
export interface ServiceJobModelChoice {
  provider: string;
  model: string;
}

/** The per-slot project policy an admin sets in the admin interface. An absent slot inherits the
 *  environment-wide model for that slot. */
export interface RunnerPolicyV2 {
  version: 2;
  slots: Partial<{
    execute: RunnerModelChoice;
    explore: RunnerModelChoice;
    generate: RunnerModelChoice;
    serviceJobs: ServiceJobModelChoice;
  }>;
}

/** A stored project policy may be either version; this client only reads it and never sends it. */
export type RunnerPolicy = RunnerPolicyV1 | RunnerPolicyV2;

/** Body for POST /projects/:id/environments (create). */
export interface CreateEnvironmentBody extends EnvironmentWriteBody {
  name: string;
}

/** Body for PATCH /projects/:id/environments/:name (edit) — name is immutable, not part of this body. */
export type UpdateEnvironmentBody = EnvironmentWriteBody;

/** Project settings on GET /projects/:id (any workspace member); masked on the list endpoint for
 *  non-admins. Mutations require shell owner/editor, platform admin, or `quality:write`. */
/** How a run dispatches its cases: all at once, or one at a time in suite order. */
export type RunExecutionMode = 'parallel' | 'sequential';

/**
 * Where a project publishes its finished runs in Report Portal. camelCase like the rest of
 * `settings`, which the quality service stores as loose jsonb.
 */
export interface ReportPortalSettings {
  enabled: boolean;
  /** The Report Portal connection to publish through; runs publish as the person who chose it. */
  connectionId: string;
  /** Name of the launch each run becomes. Absent ⇒ the quality project's name. */
  launchName?: string;
}

/** Result of POST /projects/:id/reportportal-test. `error` says why the test failed. */
export interface ReportPortalTestResult {
  ok: boolean;
  error?: string;
}

export interface ProjectSettings {
  webNetworkCaptureMode?: WebNetworkCaptureMode;
  bsRotateMinutes?: number;
  shardSize?: number;
  /** Teams incoming-webhook URL run-completion reports are posted to. */
  teamsWebhookUrl?: string;
  /** Report Portal publishing of finished runs (MEDA-847). Absent ⇒ the project does not publish. */
  reportPortal?: ReportPortalSettings;
  /** Max concurrent BrowserStack sessions for this project's account (default 2 server-side). */
  parallelSessions?: number;
  /** Mobile execution backend. `farm` uses the instance's managed Appium farm; otherwise BrowserStack. */
  mobileProvider?: 'browserstack' | 'farm';
  /** Devices (real or simulator) this project may lease on the device farm. Absent or empty ⇒ lane affinity is
   *  off and a farm run fans out exactly as it did before this feature existed. */
  devices?: DeviceRegistryEntry[];
  /** Seconds a run waits FIFO for a free lane before going terminal. Absent ⇒ the server default. */
  leaseWaitLimitSec?: number;
  /** Max lanes one run may hold at once. Absent ⇒ unlimited (greedy up to shard count). */
  maxLanesPerRun?: number;
  /**
   * Per-case time limit for every case, web or mobile, in seconds (600..10800). Absent or null ⇒ the
   * server defaults. The server caps a mobile case on BrowserStack at 6600.
   */
  caseTimeoutSec?: number | null;
  /** Attachments one case may pass to the agent (1..200). Absent or null ⇒ the server default of 50. */
  maxAttachmentsPerCase?: number | null;
  /** Whether HITL may reach a human: 'off' (default) disables value requests and video takeover;
   *  'all' permits either channel when the run has a concrete blocker. 'agent-only' is retained
   *  only for reading legacy settings and is normalized to 'off' by the settings editor. */
  hitlMode?: 'off' | 'agent-only' | 'all';
  /** Whether a run executes its cases one at a time. Absent ⇒ 'parallel' server-side. */
  executionMode?: RunExecutionMode;
  /** Whether runs are recorded for deterministic replay (Auto-Recorded Test Replay). Absent ⇒ false
   *  server-side: a project opts IN, because recording changes how a run behaves and costs more. */
  replayEnabled?: boolean;
  /** Whether the agent scrolls each checked element into view and waits for it to load before the
   *  check, so the run video shows it (MEDA-900). Applies to live, replay and exploratory runs.
   *  Absent ⇒ false server-side: a project opts IN, because each check can take up to 2 s longer. */
  waitForVisibleChecksEnabled?: boolean;
  /** Run the post-run test-quality analyzer (MEDA-352): classifies failed cases as poorly-written vs
   *  app defects and surfaces the refine flow. Absent ⇒ false server-side. */
  analyzerEnabled?: boolean;
  /** Score the agent's answers after each run (MEDA-943): cases that save the reply as `answer_text`
   *  get a 1–5 rubric and, when they have one, a checklist result. Absent ⇒ false server-side. */
  answerJudgeEnabled?: boolean;
  /** Project model policy, set by platform admins in the admin interface. This client never sends
   *  it: the server keeps a key a settings PATCH leaves out. */
  runnerPolicy?: RunnerPolicy | null;
  /** Postman-style environments for this project (Block C) — see {@link EnvironmentConfig}. */
  environments?: EnvironmentConfig[];
  /** Project-wide plain variables (Test Environments & Variables): set once, inherited by every
   *  environment unless that environment's own `vars` overrides the same key. */
  sharedVars?: Record<string, string>;
  /** Project-wide file fixtures. Written only via the file-vars upload/clear routes — never echo
   *  this map back in a settings PATCH (server preserves it). `{{key}}` → worker-local path at run. */
  sharedFileVars?: Record<string, ProjectFileVar>;
  /** Presence-only ledger for project-wide shared secret variables — the values live in Vault and
   *  are never returned. Write with `sharedSecretVars` below (this key is server-owned on read and
   *  must not be echoed back in an update body — see the client-side note in ProjectSettingsPanel). */
  sharedSecretVarKeys?: string[];
  /** Write-only per key, mirroring `EnvironmentWriteBody.secretVars`: a key mapped to `''` clears
   *  that Vault entry, a key mapped to a non-empty value (re)writes it, an omitted key is left
   *  untouched. */
  sharedSecretVars?: Record<string, string>;
  /** Project default resolved by canonical `environmentMode: 'default'` launches. */
  defaultEnvironmentName?: string;
  // The admin payload nests `accounts` under `settings` too; it is surfaced separately as
  // `Project.accounts` and must never be echoed back in an update body.
  [k: string]: unknown;
}

/** Non-secret TestRail account metadata. The api key lives in Vault — only its presence is reported. */
export interface TestRailAccountMeta {
  url: string | null;
  username: string | null;
  has_api_key: boolean;
}

/** Non-secret Zephyr Scale account metadata. The api token lives in Vault — only its presence is
 *  reported. `base_url` is the optional regional API base ('' / absent ⇒ US default). */
export interface ZephyrAccountMeta {
  base_url: string | null;
  has_api_key: boolean;
}

/** Non-secret Azure DevOps account metadata. The PAT lives in Vault — only its presence is reported. */
export interface AzureAccountMeta {
  organization: string | null;
  has_pat: boolean;
}

/** Non-secret Xray Cloud account metadata. The client secret lives in Vault — only its presence is
 *  reported. `base_url` is the optional regional API base ('' / absent ⇒ default). */
export interface XrayAccountMeta {
  client_id?: string | null;
  base_url?: string | null;
  has_client_secret?: boolean;
}

/** Non-secret BrowserStack account metadata. The access key lives in Vault — only its presence. */
export interface BrowserStackAccountMeta {
  username: string | null;
  has_access_key: boolean;
}

/** One GitHub repo wired as a generation context source. The folder to scope to is chosen per run.
 *  The shared PAT lives in Vault and is never returned — `has_token` records its presence. */
export interface GitHubRepoMeta {
  repo_url: string;
}

/** Per-project GitHub source config — admin-only on the projects payload (carries `has_token`). */
export interface ProjectGitHubAccount {
  repos: GitHubRepoMeta[];
  has_token: boolean;
}

/** Body for setting a project's GitHub source. `token` is write-only ('' clears it, omit to keep). */
export interface GitHubSourceBody {
  repos: { repoUrl: string }[];
  token?: string;
}

/** Result of POST /projects/:id/github-test — overall verdict + per-repo reachability. */
export interface GitHubTestResult {
  ok: boolean;
  message: string;
  results: { repoUrl: string; ok: boolean; error?: string }[];
}

/** One GitLab project wired as a generation context source — the GitLab twin of GitHubRepoMeta.
 *  `repo_url` accepts a full GitLab URL (self-hosted supported) or a `group/project` slug. */
export interface GitLabRepoMeta {
  repo_url: string;
}

/** Per-project GitLab source config — admin-only on the projects payload (carries `has_token`). */
export interface ProjectGitLabAccount {
  repos: GitLabRepoMeta[];
  has_token: boolean;
}

/** Body for setting a project's GitLab source. `token` is write-only ('' clears it, omit to keep). */
export interface GitLabSourceBody {
  repos: { repoUrl: string }[];
  token?: string;
}

/** Result of POST /projects/:id/gitlab-test — overall verdict + per-project reachability. */
export interface GitLabTestResult {
  ok: boolean;
  message: string;
  results: { repoUrl: string; ok: boolean; error?: string }[];
}

/** Non-secret metadata for a per-project CUSTOM external Atlassian account (Jira or Confluence). The
 *  token lives in Vault — only its presence is reported. `deployment` is derived from the URL on save
 *  (atlassian.net ⇒ cloud, else server); `username` is the Cloud account email (unused for a
 *  Server/DC PAT). */
export interface AtlassianAccountMeta {
  base_url: string | null;
  deployment: 'cloud' | 'server' | null;
  username: string | null;
  has_token: boolean;
}

/** Result of POST /projects/:id/{jira,confluence}-test — a connection verdict + a human message. */
export interface AtlassianTestResult {
  ok: boolean;
  message: string;
}

/** Per-project account overrides (admin payload only). Secrets are never returned. */
export interface ProjectAccounts {
  testrail: TestRailAccountMeta;
  zephyr?: ZephyrAccountMeta;
  azure?: AzureAccountMeta;
  xray?: XrayAccountMeta;
  browserstack: BrowserStackAccountMeta;
  github?: ProjectGitHubAccount;
  gitlab?: ProjectGitLabAccount;
  jira?: AtlassianAccountMeta;
  confluence?: AtlassianAccountMeta;
}

export interface Project {
  id: string;
  name: string;
  source_links: {
    id: string;
    source: string;
    external_project_id: string;
    created_at: string;
  }[];
  capabilities?: Capabilities;
  /** Whether a mobile run/exploration can actually launch here — mobile capability configured AND a
   *  BrowserStack account present. Distinct from `capabilities.mobile` existing: a project can have
   *  a mobile capability with no BrowserStack account, which this catches (server-computed; no
   *  secret content, sent to every user regardless of admin status). */
  mobile_runnable?: boolean;
  /** Operator settings on GET /projects/:id for workspace members (Teams webhook redacted for
   *  viewers). Masked on the list endpoint for non-admins. Managers also get `accounts`. */
  settings?: ProjectSettings | null;
  /** Per-project account overrides — present only in the admin payload (`GET /projects/:id`). */
  accounts?: ProjectAccounts;
  /** Configured GitHub repos (non-secret) — present for every member so the Generate UI can surface
   *  them as a source. The token presence lives in `accounts.github` (admin-only). */
  github_repos?: { repo_url: string }[];
  /** Configured GitLab projects (non-secret) — the GitLab twin of github_repos. */
  gitlab_repos?: { repo_url: string }[];
  /** Member-visible signal that a CUSTOM external Jira is configured (host + deployment, no token);
   *  null when none, so the Generate UI falls back to the user's corporate connector. */
  jira_source?: { host: string; deployment: 'cloud' | 'server' | null } | null;
  /** The Confluence twin of jira_source. */
  confluence_source?: {
    host: string;
    deployment: 'cloud' | 'server' | null;
  } | null;
  /** Whether the current user may launch runs here (runner grant, or admin). Gates the Launch UI. */
  can_run?: boolean;
  /** Whether the user may see *all* runs here, not just their own (view-all-runs grant, or admin).
   *  Lets the Runs tab default its My/All filter to All. */
  can_view_all_runs?: boolean;
  /** Registry workspace this project is linked to (global-workspaces phase 1); null when unlinked.
   *  The quality page uses it to scope the project selector to the globally-selected workspace. */
  workspace_id?: string | null;
  /** The viewer's own last generation-launch request (F22) — never another member's. Null when
   *  absent or when the response was not read with an authenticated viewer in context. */
  last_generation_request?: LastGenerationRequestSnapshot | null;
  created_at: string;
  updated_at: string;
}

/**
 * One generation launch's typed configuration, offered back as the prefill default. Deliberately
 * mirrors `CombinedGenerateInput`'s field names (camelCase, unlike the rest of this response) so it
 * can be spread straight into a new launch request. Query text is redacted like everywhere else it
 * is stored — restored "verbatim" means verbatim modulo credential redaction.
 */
export interface GenerationSourceControls {
  sourceControlVersion?: 1;
  useWorkspaceKnowledge?: boolean;
  knowledgeInstructions?: string;
  knowledgeMaxRequests?: number;
  knowledgeChunksPerRequest?: number;
  useArtifacts?: boolean;
  artifactIds?: string[];
  useMemory?: boolean;
  memoryShared?: boolean;
  memoryPersonal?: boolean;
}
export interface GenerationExplorationSelection {
  mode: 'custom' | 'named' | 'none';
  environmentName: string | null;
  url: string | null;
}
export interface LastGenerationRequestSnapshot {
  sourceControls?: GenerationSourceControls;
  exploration?: GenerationExplorationSelection;
  environmentName?: string | null;
  instructions: string;
  scenarioIntent?: 'standalone' | 'e2e' | 'uat';
  jiraSources: JiraRunSource[] | null;
  confluenceSources: ConfluenceRunSource[] | null;
  azureBoardsSources: AzureBoardsRunSource[] | null;
  jiraPrompt: string | null;
  confluencePrompt: string | null;
  /** Absent on a snapshot stored before the service persisted these — the prefill then falls back
   *  to `false`, matching every prompt launch predating this field. */
  jiraPromptIncludeComments?: boolean;
  confluencePromptIncludeComments?: boolean;
  githubRepos: { repoUrl: string; branch?: string; subPath?: string }[] | null;
  gitlabRepos: { repoUrl: string; branch?: string; subPath?: string }[] | null;
  maxCases: number | null;
  /** Re-resolved live on open and badged when it no longer exists. */
  credentialId: string | null;
  testRailSectionId: number | null;
  zephyrFolderId: string | null;
  azurePlanId: string | null;
  azureSuiteId: string | null;
  /** Absent on a snapshot stored before the service persisted the Xray destination — the prefill
   *  then restores nothing for it, matching every Xray launch predating these fields. */
  xrayFolderPath?: string | null;
  xrayTestSetId?: string | null;
  xrayTestPlanId?: string | null;
  /** Absent on a snapshot stored before generation could save to Quality. */
  localSuiteId?: string | null;
  updatedAt: string;
}

export interface JiraRunSource {
  jql?: string;
  includeComments?: boolean;
  maxIssues?: number;
  projectKey?: string;
  cloudId?: string;
  site?: string;
  issueTypes?: string[];
  labels?: string[];
  statuses?: string[];
}

export interface ConfluenceRunSource {
  cql?: string;
  includeComments?: boolean;
  maxPages?: number;
  spaceKey?: string;
  cloudId?: string;
  site?: string;
  labels?: string[];
}

export interface AzureBoardsRunSource {
  wiql?: string;
  project?: string;
  areaPath?: string;
  workItemTypes?: string[];
  states?: string[];
  maxItems?: number;
  includeComments?: boolean;
}

/** PATCH /projects/:id body — every field optional. `capabilities` is replaced wholesale when sent.
 *  `settings` is merged field by field: a field the body omits keeps its stored value, so a body of
 *  `{ settings: { sharedVars } }` changes only the shared text variables. `environments`, `accounts`,
 *  `sharedSecretVarKeys` and `sharedFileVars` always come from the stored row whatever the body says;
 *  `sharedSecretVars` is written to the vault key by key. Connection overrides (`accounts.*`) are no
 *  longer written from the SPA — connectors and credentials are managed in the workspace
 *  Connectors/Credentials tabs (§13). */
export interface UpdateProjectBody {
  name?: string;
  capabilities?: Capabilities;
  settings?: ProjectSettings;
}

/** The shape of a login credential. `password` covers most cases; the others tag how the secret is
 *  consumed. Mirrors the service's CredentialKind. */
export type CredentialKind =
  'password' | 'oauth' | 'jwt' | 'salesforce_jwt' | 'http_headers' | 'custom';

/**
 * A per-project login credential, metadata only — the secret lives in Vault and is never returned.
 * `created_by_name` is the author snapshot (for the "added by" label) and `can_manage` tells the UI
 * whether the current user may edit/delete this one (its author, or an admin).
 */
export interface ProjectCredential {
  id: string;
  username: string;
  alias: string | null;
  label: string;
  kind: CredentialKind;
  metadata: Record<string, unknown> | null;
  created_by: string | null;
  created_by_name: string | null;
  can_manage: boolean;
  created_at: string;
}

/** Hash algorithm for a TOTP seed (RFC 6238). Mirrors the service's TotpAlgorithm. */
export type TotpAlgorithm = 'SHA1' | 'SHA256' | 'SHA512';

/**
 * Optional TOTP (RFC 6238) enrolment on a credential. All write-only: the seed and its parameters are
 * stored in the vault beside the secret and never returned by the API. The SPA never computes a code —
 * it reads a live one from the server. Consumed by the workspace credential store
 * (`lib/api/workspace-credentials.ts`); the retired per-project quality credential routes used it too.
 */
export interface CredentialTotpFields {
  /** base32 RFC 6238 secret; write-only, never echoed back. */
  totpSeed?: string;
  totpDigits?: number;
  totpPeriod?: number;
  totpAlgorithm?: TotpAlgorithm;
}

/** Create body — `secret` is write-only (sent to Vault, never echoed back). An optional TOTP seed
 *  (with params) is enrolled alongside the secret. */
export interface CreateCredentialBody extends CredentialTotpFields {
  username: string;
  alias?: string;
  label: string;
  kind: CredentialKind;
  secret: string;
  metadata?: Record<string, unknown>;
}

/** Update body — every field optional; `secret` (when set) rotates the Vault value. username/kind
 *  are immutable server-side. Setting a `totpSeed` (or its params) merges into the Vault value — it
 *  never wipes the secret. `removeTotp` is the one exception: it wipes the enrolled seed and its
 *  params (removal wins over any totp* also sent), leaving the login without TOTP. */
export interface UpdateCredentialBody extends CredentialTotpFields {
  alias?: string;
  label?: string;
  secret?: string;
  metadata?: Record<string, unknown>;
  removeTotp?: boolean;
}

/** A live TOTP code computed server-side for enrolment confirmation. The seed stays in Vault; only
 *  the code, its window length, and the window boundary are returned. */
export interface CredentialOtpPreview {
  code: string;
  period: number;
  /** Epoch ms when the current code window ends — the UI ticks a local countdown to it. */
  expiresAt: number;
}

/** Where a test suite originates. TestRail runs + Zephyr Scale cycles + Azure DevOps Test Plan suites +
 *  Xray Cloud Test Plans/Test Sets, plus suites stored in Quality itself (`own-tms`). */
export const TEST_SOURCES = [
  'testrail',
  'zephyr',
  'azure',
  'xray',
  'own-tms',
] as const;

export type TestSource = (typeof TEST_SOURCES)[number];

/**
 * Narrow an untrusted query-param value to a known source, or undefined when it is not one. Anything
 * validating a source at runtime must use this rather than repeat the members: `TestSource` is derived
 * from `TEST_SOURCES`, so a TMS cannot be added to the type without joining the list. A hand-written
 * copy in the suite route is what dropped `source=xray` and made the service fall back to TestRail.
 */
export function asTestSource(raw: unknown): TestSource | undefined {
  if (typeof raw !== 'string') return undefined;
  const trimmed = raw.trim();
  return TEST_SOURCES.find((source) => source === trimmed);
}

/**
 * A launchable test suite shown in the "Tests" tab, tagged with the cti-quality project it maps to.
 * Source-agnostic (`source` discriminates); run-style counts are optional so own-TMS suites fit too.
 */
export interface TestSuite {
  id: string;
  source: TestSource;
  name: string;
  isCompleted?: boolean;
  passed?: number;
  failed?: number;
  untested?: number;
  createdOn: string | null;
  suiteId?: string | null;
  projectId: string;
  projectName: string;
  externalProjectId?: string;
  /** Azure suites only: the ADO Test Plan id this suite belongs to — the launch identity's second
   *  leg alongside `externalProjectId` (the ADO project) and `id` (the suite). Null/absent otherwise. */
  planId?: string | null;
  /** Azure suites only: the ADO Test Plan's display name, for the generate Plan picker label
   *  (falls back to `Plan {planId}` when absent). Null/absent otherwise. */
  planName?: string | null;
  /** Xray suites only: which Xray container this row is — a Test Plan or a Test Set. The launch
   *  identity must address the right one (`xrayTestPlanId` vs `xrayTestSetId` are mutually exclusive
   *  server-side); a suite whose kind can't be determined must be treated as not-launchable, never
   *  guessed. Null for every non-Xray source; optional/absent on servers predating this field. */
  containerKind?: 'testPlan' | 'testSet' | null;
  /** Exact provider and container identity returned by current suite discovery. */
  sourceRef?: SourceRef;
}

/** A structured API request with explicit response checks and optional captures. */
interface ApiRequestFields {
  method: 'GET' | 'POST' | 'PUT' | 'PATCH' | 'DELETE' | 'HEAD' | 'OPTIONS';
  url: string;
  headers?: Record<string, string>;
  body_format?: 'json' | 'raw' | 'form';
  body?: unknown;
  expect: { status: number; body_paths?: Record<string, unknown> };
  capture?: Record<string, string>;
  capture_secret?: Record<string, string>;
}

/** Version 1 keeps historical authentication; version 2 always carries explicit authority. */
export type ApiStep = ApiRequestFields &
  (
    | {
        version: 1;
        authentication?: 'none' | 'environment';
        credential_id?: never;
      }
    | { version: 2; authentication: 'none'; credential_id?: never }
    | { version: 2; authentication: 'workspace'; credential_id: string }
  );

/** One step of a case: the action and its expected outcome. Both are Markdown. */
export interface TestStep {
  api?: ApiStep;
  content?: string;
  expected?: string;
}

/** Canonical executable case shape used by adaptation and frozen-run APIs. */
export interface CanonicalTestCase {
  id: string;
  title?: string;
  precondition?: string;
  test_steps_text?: string;
  test_steps?: TestStep[];
  expected_result?: string;
  suite_execution_context?: string;
  dqa_adaptation?: CaseAdaptationEvidence;
  /** Verified instruction scope resolved for this case. Missing Zephyr metadata is unresolved. */
  adaptation_context?: AdaptationContextRef | AdaptationContextState;
}

export interface EditableCaseBody {
  precondition: string;
  test_steps: Array<{ content: string; expected: string; api?: ApiStep }>;
  expected_result: string;
}

export type AdaptationProvider = 'testrail' | 'azure' | 'zephyr';

export type AdaptationContextRef =
  | {
      provider: 'testrail';
      kind: 'suite';
      externalProjectId: string;
      suiteId: string;
    }
  | {
      provider: 'azure';
      kind: 'suite';
      externalProjectId: string;
      projectId: string;
      planId: string;
      suiteId: string;
    }
  | {
      provider: 'zephyr';
      kind: 'folder';
      externalProjectId: string;
      folderId: string;
    }
  | {
      provider: 'zephyr';
      kind: 'root';
      externalProjectId: string;
    };

export type AdaptationContextState =
  | {
      state: 'folder';
      context: Extract<
        AdaptationContextRef,
        { provider: 'zephyr'; kind: 'folder' }
      >;
    }
  | {
      state: 'root';
      context: Extract<
        AdaptationContextRef,
        { provider: 'zephyr'; kind: 'root' }
      >;
    }
  | {
      state: 'unresolved';
      reason: 'missing' | 'malformed' | 'unverified' | 'ownership-unresolved';
    }
  | {
      state: 'source-only';
      reason: 'foreign-project';
    };

export interface AdaptationCaseMembership {
  caseId: string;
  context: AdaptationContextRef | null;
  zephyr?: AdaptationContextState;
}

export interface CaseAdaptationSourceRef {
  workspace_id: string;
  source_instance_key: string;
  external_project_id: string;
  case_id: string;
  provider?: AdaptationProvider;
  /** Exact source container used to read the current case. */
  source_ref?: SourceRef;
  /** Legacy TestRail field retained for old responses and callers. */
  suite_id?: string;
  source_run_id?: string;
  /** Exact source-specific instruction scope used for this case. */
  context_ref?: AdaptationContextRef | null;
}

export type CaseAdaptationSource = CaseAdaptationSourceRef | SourceRef;

export interface CaseAdaptationEvidence {
  source: AdaptationProvider;
  workspace_id: string;
  source_instance_key: string;
  external_project_id: string;
  suite_id?: string;
  source_run_id?: string;
  source_hash: string;
  baseline_hash: string;
  adaptation_revision: number;
  context_revision: number;
  replay_eligible: boolean;
  warnings: string[];
  membership?: AdaptationCaseMembership;
}

export interface SuiteExecutionContextSummary {
  instructions_template: string;
  revision: number;
  /** Legacy TestRail identifier. New providers use context_ref. */
  suite_id?: string;
  provider?: AdaptationProvider;
  context_ref?: AdaptationContextRef | null;
}

export type CaseAdaptationResponse =
  | { enabled: false }
  | {
      enabled: true;
      original: CanonicalTestCase;
      baseline: CanonicalTestCase | null;
      effective: CanonicalTestCase;
      /** Display-only Markdown fields; canonical fields remain the edit/save source. */
      presentation?: {
        original: CasePresentation;
        effective: CasePresentation;
      };
      revision: number;
      source_hash: string;
      source_ref: CaseAdaptationSource;
      source_instance_key?: string;
      active: boolean;
      source_changed: boolean;
      can_edit: boolean;
      suite_context: SuiteExecutionContextSummary;
      membership?: AdaptationCaseMembership;
    };

/** Only the fields rendered by the source-case viewer. */
export type CasePresentation = Pick<
  CanonicalTestCase,
  'precondition' | 'test_steps' | 'test_steps_text' | 'expected_result'
>;

export type SuiteExecutionContextResponse =
  | { enabled: false }
  | (SuiteExecutionContextSummary & {
      enabled: true;
      can_edit: boolean;
      source_ref: CaseAdaptationSource;
      source_instance_key?: string;
    });

export interface SaveCaseAdaptationBody {
  expected_revision: number;
  expected_source_hash: string;
  source_instance_key: string;
  body: EditableCaseBody;
  provider?: AdaptationProvider;
  source_ref?: SourceRef;
  expected_source_hash_version?: number;
}

export type ResetCaseAdaptationBody = Omit<SaveCaseAdaptationBody, 'body'>;

export interface SaveSuiteExecutionContextBody {
  expected_revision: number;
  source_instance_key: string;
  instructions_template: string;
  provider?: AdaptationProvider;
  context_ref?: AdaptationContextRef;
}

export interface AdaptationWarning {
  code: string;
  case_id?: string;
  message: string;
}

/**
 * A single case's spec, presented for read-only viewing. Rich-text fields (`precondition`,
 * `steps[].*`, `stepsText`, `expectedResult`) are Markdown (converted from the source HTML
 * server-side), so the payload is plain JSON — render with a Markdown component.
 */
export interface TestSuiteCase {
  id: string;
  title?: string;
  precondition?: string;
  steps: TestStep[];
  stepsText?: string;
  expectedResult?: string;
  /** Per-case instruction scope resolved by the source adapter. */
  adaptation_context?: AdaptationContextRef | AdaptationContextState;
  /** Service membership shape used when the case came from a source hierarchy. */
  membership?: AdaptationCaseMembership;
}

/** Canonical inline-source case body accepted by the ordinary run endpoint. */
export interface InlineRunCase {
  id: string;
  title?: string;
  precondition?: string;
  test_steps_text?: string;
  test_steps?: TestStep[];
  expected_result?: string;
  custom_fields?: Record<string, unknown>;
}

export interface TestRailSection {
  id: number;
  name: string;
  parentId: number | null;
  depth: number;
}

/** The section picker's data: the project's suites, the suite read, and that suite's sections. */
export interface TestRailSectionTree {
  sections: TestRailSection[];
  suites: { id: number; name: string }[];
  suiteId: number | null;
}

// ── Refine flow (MEDA-352) ───────────────────────────────────────────────────
// The review lifecycle of a failed case flagged as poorly described: pending (queued for rewrite),
// proposed (rewrite ready to review), applied, dismissed, failed (no rewrite produced).
export type CaseRevisionStatus =
  'pending' | 'proposed' | 'applied' | 'dismissed' | 'failed';

/** One authoring problem the classification judge attributed to a poorly-written case. */
export interface CaseProblem {
  kind: string;
  stepIndex?: number | null;
  note?: string | null;
}

/** A case spec as served on a revision (raw TestCase, snake_case wire shape). */
export interface CaseSpec {
  id?: string;
  title?: string;
  precondition?: string;
  test_steps?: TestStep[];
  test_steps_text?: string;
  expected_result?: string;
}

/** A refine revision, served snake_case (see cti-quality-service serializeCaseRevision). */
export interface CaseRevision {
  id: string;
  case_id: string;
  finding_id: string | null;
  status: CaseRevisionStatus;
  original_spec: CaseSpec;
  proposed_spec: CaseSpec | null;
  problems: CaseProblem[] | null;
  error: string | null;
  created_at: string;
  updated_at: string;
}

/** The apply outcome: whether the source TMS was updated (spec is always updated for the re-run). */
export interface ApplyRevisionResult {
  tms_written: boolean;
  tms_reason: string | null;
}

/** A Zephyr Scale case folder (TestRail's section twin), served by `config/zephyr/folders`. */
export interface ZephyrFolder {
  id: number;
  name: string;
  parentId: number | null;
}

/** An Xray Cloud test-repository folder, served by `config/xray/folders`. Xray folders carry no id
 *  of their own — `path` is the identity, and `parent_path` (the path minus its last segment) is what
 *  lets a caller derive the tree order, unlike ZephyrFolder's numeric `id`/`parentId` pair. */
export interface XrayFolder {
  path: string;
  name: string;
  parent_path: string | null;
  tests_count: number;
}

/** Global quality settings. `webEnabled` gates whether the Quality section shows in this interface. */
export interface QualityConfig {
  webEnabled: boolean;
  /** Whether TestRail credentials (TESTRAIL_URL/USERNAME/API_KEY) are configured on the service. */
  testRailConfigured: boolean;
}

/** A test suite's content: display name, owning project, and its presented cases. */
export interface TestSuiteCases {
  id: string;
  source: TestSource;
  name: string;
  projectId: string | null;
  projectName: string | null;
  cases: TestSuiteCase[];
  /** The external project this suite lives in at its source (e.g. the ADO project for an Azure
   *  suite). Feeds the launch identity alongside `planId`. Empty/absent for sources with no such
   *  concept. */
  externalProjectId?: string;
  /** Azure suites only: the ADO Test Plan id this suite belongs to — the launch identity's second
   *  leg. Null/absent for every other source. */
  planId?: string | null;
  /** Same discriminator as `TestSuite.containerKind` — the launch descriptor rebuilt from this
   *  response needs it too. */
  containerKind?: 'testPlan' | 'testSet' | null;
  /** Exact provider and container identity returned by current suite discovery. */
  sourceRef?: SourceRef;
}

/** A flat suite of test cases stored in Quality itself (`GET /local-suites`). */
export interface LocalSuite {
  id: string;
  name: string;
  project_id: string;
  case_count: number;
  execution_instructions_revision: number;
  /** The own-tms launch identity; its inner keys are camelCase like every other `SourceRef`. */
  source_ref: SourceRef;
  can_edit: boolean;
  created_at: string;
  updated_at: string;
}

/** One case stored in Quality. `id` addresses it in REST; `key` (`Q-<n>`) is what a run selects. */
export interface LocalCase {
  id: string;
  key: string;
  suite_id: string;
  project_id: string;
  title: string;
  precondition: string;
  test_steps: TestStep[];
  expected_result: string;
  position: number;
  revision: number;
  /** The committed outcome of the latest run that executed this case; null until it has run. */
  last_verdict: CaseOutcome | null;
  last_run_id: string | null;
  last_run_at: string | null;
  updated_at: string;
  updated_by: string;
  can_edit: boolean;
}

/** The editable body of a stored case, as sent on create and edit. */
export interface LocalCaseBody {
  title: string;
  precondition: string;
  test_steps: TestStep[];
  expected_result: string;
}

export interface LocalCasePage {
  items: LocalCase[];
  next_cursor: string | null;
}

// ── Native JSON report ───────────────────────────────────────────────────────
// The worker emits the combined report as JSON under runs/{id}/report/*.json; the SPA renders it
// natively (Cases + Video tabs). Shapes mirror the service contract; only the subset the web
// interface renders is modelled here (summary + cases).

export interface ReportTotals {
  count: number;
  passed: number;
  failed: number;
  broken: number;
  // Cases that started but ran out of time. Counts as a failure for the run outcome; own bucket.
  timeout?: number;
  pass_rate: number;
  total_duration_ms: number;
  total_cost_usd: number;
  /** False when a partial report could not reliably aggregate cost; absent on older reports. */
  total_cost_recorded?: boolean;
}

/** A BrowserStack session (mobile) with its recording and the cases it covered. */
export interface ReportSession {
  session_id: string;
  video: { kind: 'url'; value: string } | null;
  case_ids: string[];
  /**
   * Recording readiness (schema 2). Mobile video is session-grained: while a session is open the
   * recording isn't resolvable, so every case of it shows 'pending' until the session closes and they
   * flip to 'ready' together. Absent ⇒ 'ready' (schema-1 reports).
   */
  status?: 'pending' | 'ready';
}

export interface ReportSummary {
  schema_version: number;
  run_id: string;
  platform: Platform;
  run_date: string | null;
  project_name: string | null;
  totals: ReportTotals;
  /** Separate executor-request estimate; absent on legacy report bundles. */
  execution_estimate?: ExecutionEstimateV1;
  sessions?: ReportSession[];
  /** Components a multi-component run exercised (union across its cases, e.g. ['web','mobile']).
   *  Absent on single-component runs and servers predating the pre-scan. */
  components?: string[];
  /** Canonical documents carry the same revision across the whole bundle. */
  report_revision?: number;
  outcome_protocol_version?: 1 | null;
  execution_status?: ExecutionStatus;
  results?: OutcomeResults;
  unconfirmed_qa_errors?: number;
  history?: HistoricalRunProvenance;
  status_caption?: string | null;
  completion_warning?: 'cleanup_unconfirmed' | null;
}

/** A per-step screenshot (or other artifact) referenced by its run-relative S3 key. */
export interface CaseAttachment {
  /** Always 'image' today (screenshots). */
  type: string;
  name: string;
  /** S3 key relative to runs/{id}/ — fetch via the /artifacts/* endpoint. */
  key: string;
}

/** One failed HTTP exchange recorded during a step, captured deterministically from a Playwright
 *  HAR. Evidence only — the product assigns no fault. `host` is never filtered to "the
 *  application's own" host: an SSO-first case or an app whose API lives on another host would
 *  otherwise silently lose the exact exchange this feature exists to show, so every failed
 *  exchange rides through with its own host and the reader attributes it. `path` never carries a
 *  query string; `message` is truncated to 300 chars server-side. `occurrences` counts identical
 *  `(host, method, path, status)` entries collapsed into this one; absent/1 means it happened
 *  once. Cast from the API, not validated. */
export interface ApplicationExchange {
  host: string;
  method: string;
  path: string;
  status: number;
  message: string;
  occurrences?: number;
}

/** One API call a step made, as the worker summarized it for the report (MEDA-914). The full
 *  masked record (headers, whole bodies) is a JSON artifact at `artifact_key`, relative to
 *  `runs/{id}/`, loaded on demand. `status` is null and `error` set when no response arrived.
 *  Previews are cut to 2,000 (request) and 4,000 (response) characters; the `*_truncated` flags
 *  say when. `id` (`C101-3-1`, or `C101-P1-1` for a precondition) names the call in the run's
 *  API log and the PDF. Cast from the API, not validated. */
export interface ApiCallSummary {
  id: string;
  method: string;
  host: string;
  path: string;
  status: number | null;
  error: string | null;
  duration_ms: number | null;
  request_body_preview: string;
  request_body_preview_truncated: boolean;
  body_preview: string;
  body_preview_truncated: boolean;
  body_truncated: boolean;
  body_bytes: number;
  artifact_key: string;
}

/** One PDF read or render the agent did in a step (MEDA-1139). The masked record (text excerpt) is
 *  a JSON artifact at `artifact_key` and the rendered page a PNG at `png_key`, both relative to
 *  `runs/{id}/` and loaded on demand; either key may be absent. Cast from the API, not validated. */
export interface DocumentEvidenceSummary {
  file: string;
  sha256: string;
  /** PDF reads only; spreadsheet entries carry `sheet` and `rows` instead. */
  pages?: number[];
  /** Spreadsheet reads only: the sheet name; absent for csv. */
  sheet?: string;
  /** Spreadsheet reads only: the first and last row read. */
  rows?: [number, number];
  png?: string;
  artifact_key?: string;
  png_key?: string;
}

export interface CaseStepDetail {
  name: string;
  status: string;
  observation: string;
  attachments: CaseAttachment[];
  /** Zero-based authored test-step index; preconditions and older bundles may omit it. */
  step_index?: number | null;
  /** Wall-clock time the step took (ms); null/absent when the source step carried no start+stop
   *  (a live step, or a run whose fragment predates per-step timing — no backfill). */
  duration_ms?: number | string | null;
  /** Offset (ms) of the step within its video; null when no seek anchor is available. */
  timecode_ms?: number | null;
  /** The recording `timecode_ms` is measured in, named only when the case recorded several files
   *  (`video_parts`). Absent ⇒ the case's own recording. */
  video_key?: string;
  /** Failed application HTTP exchanges bound to this step's time window; absent/empty when the
   *  step had none, carried no HAR (a hard-killed case), or predates this field. */
  app_exchanges?: ApplicationExchange[];
  /** The API calls this step made, in order; absent when it made none or predates the field. */
  api_calls?: ApiCallSummary[];
  /** The PDFs the agent read or rendered in this step; absent when it opened none. */
  documents?: DocumentEvidenceSummary[];
  /** Whether the worker captured this step's screenshot: `unavailable` means it was expected but is
   *  missing, `not_applicable` means the step takes none. Absent on runs that predate the field. */
  evidence_status?: 'captured' | 'unavailable' | 'not_applicable';
  /** The component this step actually ran on ('web' | 'mobile'), attributed by the executor. Drives
   *  the per-step platform badge and routes "jump to this moment" to that component's recording.
   *  Absent on single-component runs and older reports → no badge, seek falls back to the run's
   *  platform. Cast from the API, not validated. */
  component?: string;
  /** Wave 2 (`wave2-contract.md` #2): the expectation fragment the agent checked and what it
   *  actually observed, present on a step the verdict rule evaluated for a cosmetic mismatch.
   *  Absent on every step the rule never looked at and on servers predating wave 2. */
  expected_text?: string | null;
  actual_text?: string | null;
  /** True once the service has downgraded this step from `failed` to `passed` because
   *  `expected_text`/`actual_text` normalize equal (a punctuation/case/whitespace-only mismatch).
   *  Absent everywhere else. */
  deviation?: boolean;
}

/**
 * A per-case recording. web → an object-storage key (the case owns its video). mobile → a reference
 * to the shared BrowserStack session video, with this case's window inside it (`clip_*`). `status`
 * (schema 2) is the recording readiness; absent ⇒ 'ready' (schema-1 reports).
 */
export type CaseVideo =
  | { kind: 's3'; key: string; status?: 'pending' | 'ready' }
  | {
      kind: 'session';
      session_id: string;
      value: string | null;
      clip_start_ms?: number | null;
      clip_end_ms?: number | null;
      status?: 'pending' | 'ready';
    };

/**
 * One file of a web case that recorded several: Playwright writes one per page, so a second tab or
 * the fresh browser context of a completion recovery adds a file. `duration_ms` is null when the
 * worker could not read it.
 */
export type CaseVideoPart = {
  kind: 's3';
  key: string;
  status?: 'pending' | 'ready';
  duration_ms?: number | null;
};

/**
 * A per-component recording for a multi-component case, attributed by the executor's component
 * adapter. `report_shape` says how the component records: 'video-s3' (web .webm key), 'video-session'
 * (a BrowserStack session ref), 'trace' or 'none' (no player). `video` is null for trace/none.
 */
export interface ComponentArtifact {
  component: string;
  report_shape: 'video-s3' | 'video-session' | 'trace' | 'none';
  video: CaseVideo | null;
}

/**
 * One execution attempt of a case (report schema 3). A run with no re-queues has a single
 * (synthesized) attempt; a re-queued case has one entry per attempt, each with its own
 * steps/status/cost/duration/error/video. The case's top-level fields are the FINAL verdict (last
 * attempt's status) and the Σ of cost over all attempts.
 */
export interface CaseAttempt {
  skill_execution?: QualitySkillExecution;
  attempt: number;
  status: ResultStatus;
  duration_ms: number | string | null;
  cost_usd: number | string | null;
  /** Separate from cost_usd; omitted on legacy report attempts. */
  execution_estimate?: ExecutionEstimateV1;
  /** False when the attempt ended before cost could be measured; absent means legacy/current behavior. */
  cost_recorded?: boolean;
  steps: CaseStepDetail[];
  error?: { message: string } | null;
  video?: CaseVideo | null;
  /** Per-component recordings for a multi-component run; absent ⇒ read the scalar `video`. */
  videos?: ComponentArtifact[];
  /** Every file of a web attempt that recorded more than one, oldest first; absent ⇒ one file. */
  video_parts?: CaseVideoPart[];
  /** Null means the immutable attempt has no canonical mapping; legacy evidence is preserved. */
  outcome?: CaseOutcome | null;
  outcome_provenance?: {
    source: 'legacy_status_unmapped';
    status: string;
  } | null;
}

export interface CaseDetail {
  skill_execution?: QualitySkillExecution;
  /** The status to show; absent on an older service. */
  display_status?: PresentedCaseStatus;
  id: string;
  title: string | null;
  status: ResultStatus;
  duration_ms: number | null;
  cost_usd: number | null;
  /** Separate from cost_usd; omitted on legacy report cases. */
  execution_estimate?: ExecutionEstimateV1;
  /** False when the case ended before cost could be measured; absent means legacy/current behavior. */
  cost_recorded?: boolean;
  steps: CaseStepDetail[];
  error: { message: string } | null;
  video: CaseVideo | null;
  /** Per-component recordings for a multi-component run; absent ⇒ read the scalar `video`. */
  videos?: ComponentArtifact[];
  /** Every file of a web case that recorded more than one, oldest first; absent ⇒ one file. */
  video_parts?: CaseVideoPart[];
  // Per-attempt history (schema 3). Always ≥1 when present; absent on schema ≤2 reports → render flat.
  attempts?: CaseAttempt[];
  // How the case ran (Auto-Recorded Test Replay): 'replayed' = deterministic replay, 'healed' =
  // replay went stale then the LLM reran, 'llm' = normal LLM. Null/absent on non-replay or older
  // runs. API-cast, so tolerate an unknown value.
  execution_mode?: ExecutionMode | null;
  case_execution_id?: string;
  attempt?: number | null;
  history?: HistoricalCaseProvenance;
  outcome?: CaseState;
  outcome_revision?: number;
  outcome_finalized_at?: string | null;
  needs_triage?: boolean;
  triage_reason?: TriageReason | null;
  classification?: OutcomeClassification | null;
}

export type ExecutionMode = 'replayed' | 'llm' | 'healed';

export interface ReportCases {
  schema_version: number;
  run_id: string;
  cases: CaseDetail[];
  report_revision?: number;
  outcome_protocol_version?: 1 | null;
}

/** One event from a case's agent conversation, surfaced in the Error Digest's "last events". */
export interface ConversationEvent {
  kind: 'tool_call' | 'text' | 'result' | 'error' | 'final';
  name?: string;
  input?: string;
  content?: string;
  id?: string;
  tool_use_id?: string;
  cost?: number;
  duration_ms?: number;
}

export interface ErrorDigestFailed {
  id: string;
  case_id?: string;
  title?: string | null;
  status?: string;
  error?: string;
  last_events?: ConversationEvent[];
  outcome?: CaseOutcome;
  outcome_revision?: number;
  needs_triage?: boolean;
  triage_reason?: TriageReason | null;
  classification?: OutcomeClassification | null;
  display_status?: PresentedCaseStatus;
}

export type ReportErrorDigest = {
  schema_version: number;
  run_id: string;
  run_date?: string | null;
  failed?: ErrorDigestFailed[];
  report_revision?: number;
  outcome_protocol_version?: 1 | null;
  results?: OutcomeResults;
  unconfirmed_qa_errors?: number;
  history?: HistoricalRunProvenance;
} & Partial<Record<CaseOutcome | 'assessing' | 'not_run', ErrorDigestFailed[]>>;

export type OutcomeReassessmentStatus =
  | 'queued'
  | 'running'
  | 'applied'
  | 'unresolved'
  | 'failed'
  | 'expired'
  | 'superseded';

export interface OutcomeReassessmentReceipt {
  request_id: string;
  run_id: string;
  case_id: string;
  case_execution_id: string;
  base_revision: number;
  status: OutcomeReassessmentStatus;
  reason: string | null;
  deadline_at: string;
  created_at: string;
  completed_at: string | null;
  outcome_revision: number | null;
  report_revision: number | null;
  actor_id: string;
  model: string | null;
  rubric_version: string | null;
  input_digest: string | null;
  before: unknown;
  after: unknown;
}

export interface OutcomeReassessmentEligibility {
  eligible: boolean;
  reason: string | null;
  current_revision: number | null;
  active_request_id: string | null;
}

export interface OutcomeReassessmentState {
  eligibility: OutcomeReassessmentEligibility;
  receipts: OutcomeReassessmentReceipt[];
  active: OutcomeReassessmentReceipt | null;
  total: number;
}

export interface CreateOutcomeReassessmentBody {
  caseId: string;
  expectedRevision: number;
  requestId: string;
}

export interface CreateBulkOutcomeReassessmentBody {
  batchId: string;
}

/** One case the batch refused to start, with the eligibility reason that refused it. */
export interface SkippedOutcomeReassessment {
  case_id: string;
  reason: string | null;
}

export interface BulkOutcomeReassessmentResult {
  batch_id: string;
  started: OutcomeReassessmentReceipt[];
  skipped: SkippedOutcomeReassessment[];
}

/** The subset of the native report the web interface renders: summary (sessions) + cases + errors. */
export interface ReportBundle {
  summary: ReportSummary | null;
  cases: ReportCases | null;
  errors: ReportErrorDigest | null;
  /** A canonical bundle is exportable only when every available document agrees on this revision. */
  reportRevision?: number | null;
  coherence?: 'legacy' | 'coherent' | 'changed' | 'unavailable';
}

export interface SuiteLaunchSettings {
  execution_mode: RunExecutionMode;
  execution_mode_source: 'suite' | 'project' | 'default';
  saved_execution_mode: RunExecutionMode | null;
  revision: number;
  source_key: string;
  can_manage_defaults: boolean;
}

export interface CreateRunBody {
  testRailResultsTarget?: 'existing_run' | 'new_run';
  testRailNewRunName?: string;
  webNetworkCaptureMode?: WebNetworkCaptureOverride;
  source?:
    'testrail' | 'zephyr' | 'azure' | 'xray' | 'own-tms' | 'mock' | 'inline';
  platform?: Platform;
  /** Canonical external provider/container identity. Inline and mock launches never carry it. */
  sourceRef?: SourceRef;
  /** Exact case ids inside `sourceRef`, or from the current source for failed-only
   *  current-version reruns; omission launches the complete container. */
  selectedCaseIds?: string[];
  /** Canonical environment intent. A name is present only with `named`. */
  environmentMode?: EnvironmentMode;
  /** Restricts automatic case routing to the selected project platforms. */
  targetPlatforms?: Platform[];
  projectId?: string;
  testRailRunId?: string;
  testRailSuiteId?: string;
  /** Zephyr source: launch from a test cycle (write-back target) — its KEY, e.g. "ABC-R40". */
  zephyrCycleKey?: string;
  /** Zephyr source: launch from a folder of cases (read-only, no write-back). */
  zephyrFolderId?: string;
  /** Azure source: the three-part launch identity — the ADO project, the Test Plan id, and the Test
   *  Suite id. All three are required together (server-enforced). */
  azureProject?: string;
  azurePlanId?: string;
  azureSuiteId?: string;
  /** Xray source: the Jira project key the tests live in, plus exactly one of the Test Plan or Test
   *  Set issue id/key whose tests the run executes (mutually exclusive, server-enforced). */
  xrayProjectKey?: string;
  xrayTestPlanId?: string;
  xrayTestSetId?: string;
  suite?: string;
  cases?: InlineRunCase[];
  caseCount?: number;
  parallelism?: number;
  /** Per-run override of the project's case execution mode; omitted inherits the project setting. */
  executionMode?: RunExecutionMode;
  /** Runs every case this many times, 1..50 (MEDA-1288). Sent only when greater than 1. */
  repeatCount?: number;
  /** What a repeated run does after a failed iteration. Sent only with `repeatCount`. */
  onIterationFailure?: IterationFailurePolicy;
  expectedSuiteSettingsRevision?: number;
  model?: string;
  capability?: Capability;
  /** Environment name for canonical `named` mode or legacy name-only requests. */
  environmentName?: string;
  /** Plain variable values for this run only (MEDA-865). Sent only when the service advertises
   *  `variable_overrides`; omitted when nothing is overridden. */
  variableOverrides?: Record<string, string>;
  /** Optional per-run video takeover permission. Omitted inherits the workspace setting; false is
   *  an explicit override and must remain distinct from omission. */
  takeoverEnabled?: boolean;
  /** Overrides both human input and video takeover for this run. */
  humanInteractionEnabled?: boolean;
  /** Bound acknowledgement returned by the matching preview when source drift is present. */
  driftConfirmationToken?: string;
  skillRevisionHash?: string;
  /** Stable client idempotency key. Keep this value for transport retries of the same preview. */
  launchRequestKey?: string;
}

/** Proposed source and execution settings used to bind launch admission to its preview.
 *  Project identity is supplied by the route; case resolution stays server-side. */
export type RunPreflightBody = Pick<
  CreateRunBody,
  | 'source'
  | 'targetPlatforms'
  | 'humanInteractionEnabled'
  | 'webNetworkCaptureMode'
  | 'testRailRunId'
  | 'testRailSuiteId'
  | 'zephyrCycleKey'
  | 'zephyrFolderId'
  | 'azureProject'
  | 'azurePlanId'
  | 'azureSuiteId'
  | 'xrayProjectKey'
  | 'xrayTestPlanId'
  | 'xrayTestSetId'
  | 'sourceRef'
  | 'selectedCaseIds'
  | 'environmentMode'
  | 'environmentName'
  | 'variableOverrides'
  | 'executionMode'
  | 'repeatCount'
  | 'onIterationFailure'
  | 'testRailResultsTarget'
  | 'testRailNewRunName'
>;

/** Where in the workspace settings a variable the cases use gets its value (MEDA-865). */
export type CaseVariableOrigin =
  'environment' | 'project' | 'secret' | 'file' | 'base_url';

/** One workspace variable the cases of a launch use, with its workspace value (MEDA-865). */
export interface CaseVariable {
  key: string;
  /** Null for a secret or a file, whose value never leaves the service. */
  value: string | null;
  origin: CaseVariableOrigin;
  /** Whether the service accepts a launch override of this key. */
  overridable: boolean;
}

/** Result of the launch-preflight endpoint: the `{{tokens}}` that won't resolve under the given
 *  environment (sent literally if the run launches as-is) plus how many did resolve. Advisory only —
 *  a non-empty `unresolved` never blocks Launch. */
export interface RunPreflightResult {
  case_count?: number;
  execution_mode?: 'parallel' | 'sequential';
  execution_mode_source?: 'run_override' | 'suite' | 'project' | 'default';
  suite_settings_revision?: number | null;
  skill_revision_hash?: string;
  skills?: QualitySkillPreview[];
  unresolved: string[];
  resolvedCount: number;
  /** The workspace variables the cases use. Absent from a service that predates the list. */
  variables?: CaseVariable[];
  warnings?: AdaptationWarning[];
  drift_confirmation_token?: string;
  cases?: CanonicalTestCase[];
  source_changed?: boolean;
  /** Whether any resolved case uses `{{ITERATION}}` or `{{UNIQUE}}` (MEDA-1288). */
  iterationTokensUsed?: boolean;
}

export interface RunLaunchStatus {
  id: string;
  state: string;
  run_id?: string;
  error_code?: string;
}

/** Source kind a regression job's saved run-template launches from (mirrors the service `SourceKind`). */
export type RegressionSource = NonNullable<CreateRunBody['source']>;

/**
 * The saved run-template of a regression job — a `CreateRunDto` subset the fire endpoint replays.
 * Stored server-side as opaque jsonb, so its inner keys stay camelCase even inside the snake_case
 * job envelope; the query layer passes this value through untouched (never deep-cased). It carries
 * no model: each fire resolves the project or environment model.
 */
export interface RegressionRunConfig extends Omit<
  CreateRunBody,
  'projectId' | 'source' | 'cases' | 'model'
> {
  configVersion?: 2;
  source: RegressionSource;
  cases?: Array<InlineRunCase & { provenance?: Record<string, unknown> }>;
  inlineSnapshot?: { revision: number; contentHash: string };
}

/** Null resets these defaults only on a saved-template update. */
export type RegressionRunConfigPatch = Omit<
  RegressionRunConfig,
  'targetPlatforms' | 'parallelism'
> & {
  targetPlatforms?: Platform[] | null;
  parallelism?: number | null;
};

/** `synced` = the scheduler automation exists and is linked; `pending` = its create failed and the
 *  creator can retry (the job persisted but has no automation yet). */
export type SchedulingStatus = 'synced' | 'pending';

/**
 * A regression job as the SPA consumes it (camelCase), mapped from the snake_case list/get row. A
 * `null` cron means the job is manual (run-only, no schedule). `createdBy` is the creator's platform
 * JWT `sub` — schedule-mutation controls are gated to that user client-side (the backend 403s anyway).
 */
export interface RegressionJob {
  requestKey?: string | null;
  provisioningState?: string;
  id: string;
  name: string;
  projectId: string;
  runConfig: RegressionRunConfig;
  cron: string | null;
  tz: string;
  enabled: boolean;
  createdBy: string;
  schedulerAutomationId: string | null;
  schedulingStatus: SchedulingStatus;
  lastRunId: string | null;
  createdAt: string;
  updatedAt: string;
  /** Block B: Teams-notify-the-creator on-fail leg. */
  notifyCreatorOnFail: boolean;
  /** Block B: whether the signed external callback leg is configured. The URL/secret themselves are
   *  never sent to the browser — only the masked hint and runtime health below. */
  callbackEnabled: boolean;
  callbackUrlMasked: string | null;
  /** True once 3 consecutive callback sends have failed; the leg stops firing until re-enabled. */
  callbackDisabled: boolean;
  callbackFailureCount: number;
  callbackLastError: string | null;
}

/** The snake_case job row the service returns from list/get/create/update. */
export interface RegressionJobRow {
  request_key?: string | null;
  provisioning_state?: string;
  id: string;
  name: string;
  project_id: string;
  run_config: RegressionRunConfig;
  cron: string | null;
  tz: string;
  enabled: boolean;
  created_by: string;
  scheduler_automation_id: string | null;
  scheduling_status: SchedulingStatus;
  last_run_id: string | null;
  created_at: string;
  updated_at: string;
  notify_creator_on_fail: boolean;
  callback_enabled: boolean;
  callback_url_masked: string | null;
  callback_disabled: boolean;
  callback_failure_count: number;
  callback_last_error: string | null;
}

/**
 * One entry of a job's run history from `GET /regression/:id/runs`. Stays snake_case (like `Run`),
 * consumed as-is — the SPA renders these read-only and links each to `/quality/:id`.
 */
export interface RegressionRunSummary {
  id: string;
  /** Present only for explicit null-protocol history. Canonical history omits this alias. */
  status?: RunStatus;
  outcome_protocol_version?: 1 | null;
  execution_status?: ExecutionStatus;
  /** See `Run.badge_status` — the value a client should render as the badge. Same optionality and
   *  same rule (`run.badge_status ?? run.status`). */
  badge_status?: RunStatus;
  results?: OutcomeResults;
  total?: number;
  report_revision?: number | null;
  status_caption?: string | null;
  completion_warning?: 'cleanup_unconfirmed' | null;
  delivery_summary?: DeliverySummary;
  summary?: RunSummary;
  source: string | null;
  run_number: number | null;
  created_at: string;
  finished_at: string | null;
}

/** Body for `POST /regression` (camelCase). `cron` omitted/null = a manual (run-only) job.
 *  `callbackUrl: null` clears/disables the callback leg; omitting it (undefined) leaves any stored
 *  callback untouched. `callbackSecret` is write-only — the server never echoes it back. */
export interface CreateRegressionJobBody {
  requestKey?: string;
  name: string;
  projectId: string;
  runConfig: RegressionRunConfig;
  cron?: string | null;
  tz?: string;
  enabled: boolean;
  notifyCreatorOnFail?: boolean;
  callbackUrl?: string | null;
  callbackSecret?: string;
}

/** Body for `PATCH /regression/:id` (camelCase). Sending only `{ enabled }` toggles pause/unpause. */
export interface UpdateRegressionJobBody {
  name?: string;
  runConfig?: RegressionRunConfigPatch;
  cron?: string | null;
  tz?: string;
  enabled?: boolean;
  notifyCreatorOnFail?: boolean;
  callbackUrl?: string | null;
  callbackSecret?: string;
}

/** POST /runs/explore — launch an exploratory-testing run (the Explorer) that synthesizes test
 *  cases into TestRail. Web only. The TestRail project is resolved server-side from the project's
 *  source_links; only an explicit numeric section id travels in the body. */
export interface CreateExploreRunBody {
  webNetworkCaptureMode?: WebNetworkCaptureOverride;
  appUrl: string;
  /** CTI project id — scopes the run and resolves the TestRail project via source_links. */
  projectId?: string;
  model?: string;
  maxCases?: number;
  /** Extra instructions / focus hints sent verbatim into the exploration prompt. */
  instructions?: string;
  /** Optional project credential to log in with before exploring (must belong to the project). */
  credentialId?: string;
  /** Explicit TestRail section id; worker skips section resolution when set. */
  testRail?: { sectionId?: number };
}

/** One text reference grounding a generated case in a source: the source as the generator named it
 *  (a document filename, a Jira key, a Confluence page title, or "Live app"), an optional location
 *  within it, and an optional short verbatim quote. Plain text — no URLs in v1. */
export interface Citation {
  source: string;
  location?: string;
  quote?: string;
}

/** One worker log line for a run: a monotonic sequence `n` (for de-duping live + backfilled lines),
 *  an epoch-ms timestamp `t`, a source kind `s` ('sys' | 'out' | 'err'), and the message `m`. Seeded
 *  from GET /runs/:id/logs and appended live from the SSE `log.appended` event. */
export interface RunLogLine {
  n: number;
  t: number;
  /** Source kind: worker lifecycle ('sys') vs executor stdout/stderr ('out' | 'err'). */
  s: 'sys' | 'out' | 'err';
  m: string;
}

/** A synthesized case as proposed for review (mirrors the worker/service GeneratedTestCase shape). */
export type StoryLinkingProvider = 'azure' | 'testrail' | 'zephyr' | 'xray';
export type StoryLinkingOutputProvider = StoryLinkingProvider | 'unknown';
/** Where generated cases can be published: a TMS, or a suite stored in Quality. */
export type PublicationProvider = StoryLinkingProvider | 'own-tms';
export type StoryLinkSource = 'azure_boards' | 'jira';

/** A candidate story/requirement link frozen by the service for reviewer approval. */
export interface ProposedStoryLink {
  ref: string;
  source: StoryLinkSource;
  externalId: string;
  key: string;
  title: string;
  url?: string;
  type?: string;
  state?: string;
}

/** Informational reason shown when story linking was skipped or discovery failed. */
export interface StoryLinkingAlert {
  code: string;
  message: string;
}

/** Frozen story-link discovery metadata returned on a generation proposal. */
export interface StoryLinkingProposalMeta {
  requested: boolean;
  outputProvider: StoryLinkingOutputProvider;
  status:
    | 'ready'
    | 'skipped_missing_jira'
    | 'skipped_unsupported'
    | 'discovery_failed';
  candidateCount: number;
  truncated: boolean;
  alert?: StoryLinkingAlert;
  candidates?: ProposedStoryLink[];
}

export type PublicationCaseState =
  'pending' | 'created' | 'failed' | 'ambiguous' | 'skipped_existing';
export type PublicationPlacementState =
  'pending' | 'succeeded' | 'failed' | 'ambiguous' | 'skipped';
export type PublicationRelationState =
  'pending' | 'succeeded' | 'failed' | 'ambiguous' | 'skipped';

export interface PublicationRelationOutcome {
  ref: string;
  state: PublicationRelationState;
  attempts: number;
  reason_code: string | null;
}

/** Safe snake_case projection of one durable generated-case publication row. */
export interface GeneratedCasePublication {
  id: string;
  batch_id?: string | null;
  proposal_id: string;
  run_id: string;
  case_index: number;
  selected_case?: ProposalCase | null;
  provider: PublicationProvider;
  case_state: PublicationCaseState;
  placement_state: PublicationPlacementState;
  external_case_id: string | null;
  external_case_key: string | null;
  retained_links: ProposedStoryLink[];
  relation_outcomes: PublicationRelationOutcome[];
  case_error_reason_code: string | null;
  /** The upstream provider's own words, redacted at the server boundary — never assumed complete
   *  (a bare provider token can survive). Leads with a policy warning for an ambiguous outcome. */
  case_error_message: string | null;
  placement_error_reason_code: string | null;
  placement_error_message: string | null;
  lock_version: number;
  created_at: string;
  updated_at: string;
}

/** An immutable existing TMS destination selected for one publication batch. */
export type GeneratedCasePublicationDestination =
  | {
      provider: 'own-tms';
      suiteId: string;
      suiteName?: string;
    }
  | {
      provider: 'azure';
      project: string;
      planId: string;
      suiteId: string;
      planName?: string;
      suiteName?: string;
    }
  | {
      provider: 'testrail';
      projectId: string;
      sectionId: number;
      sectionName?: string;
    }
  | {
      provider: 'zephyr';
      projectKey: string;
      folderId?: string | null;
      folderName?: string;
    }
  | {
      provider: 'xray';
      projectKey: string;
      folderPath?: string | null;
      testSetId?: string | null;
      testPlanId?: string | null;
      label?: string;
      newContainerKind?: 'testSet' | 'testPlan';
      newContainerSummary?: string;
    };

/** Serialized destination snapshot returned with a durable publication batch. */
export type GeneratedCasePublicationDestinationSnapshot =
  | {
      provider: 'own-tms';
      suite_id: string;
      suite_name?: string | null;
    }
  | {
      provider: 'azure';
      project: string;
      plan_id: string;
      suite_id: string;
      plan_name: string | null;
      suite_name: string | null;
    }
  | {
      provider: 'testrail';
      project_id: string;
      section_id: number;
      section_name: string | null;
    }
  | {
      provider: 'zephyr';
      project_key: string;
      folder_id: string | null;
      folder_name: string | null;
    }
  | {
      provider: 'xray';
      project_key: string;
      folder_path: string | null;
      test_set_id: string | null;
      test_plan_id: string | null;
      label: string | null;
    };

export interface GeneratedCasePublicationBatch {
  generation_snapshot?: {
    schemaVersion: 1;
    variables: {
      key: string;
      defaultValue: string;
      outcome: 'created' | 'reused';
      caseIndices: number[];
    }[];
  } | null;
  id: string;
  proposal_id: string;
  run_id: string;
  provider: PublicationProvider;
  destination: GeneratedCasePublicationDestinationSnapshot;
  include_suggested_story_links: boolean;
  status: 'publishing' | 'succeeded' | 'partial' | 'failed';
  publication_ids: string[];
  created_at: string;
  updated_at: string;
}

export interface ProposalCase {
  generationWarnings?: string[];
  generationFormat?: 1 | 2;
  basis?: GenerationCaseBasis[];
  preparation?: {
    summary: string;
    role: string;
    state: string;
    data: string;
    actions: { id: string; content: string; expected: string }[];
    gaps: { kind: string; description: string }[];
  };
  title: string;
  precondition?: string;
  test_steps?: string;
  test_steps_separated?: TestStep[];
  expected_result?: string;
  /** What requirement/behavior this case verifies (plain language), and the sources it cites. */
  coverage?: string;
  citations?: Citation[];
  /** Stable ids from the service-owned frozen requirement inventory. */
  requirementIds?: string[];
  /** Provider-neutral metadata. The service maps only values discovered as valid for the target. */
  priority?: string | null;
  type?: string | null;
  area?: string | null;
  /** Canonical story links proposed by discovery and editable only by removal in v1. */
  storyLinks?: ProposedStoryLink[];
}

export type RequirementCoverageStatus = 'covered' | 'partial' | 'uncovered';

export interface SelectedJiraSourceSnapshot {
  sourceKind: 'jira_issue' | 'jira_query';
  rootIssueKey: string | null;
  rootIssueId: string | null;
  /** Null when a bare JQL source does not identify one Jira project. */
  projectKey: string | null;
  projectName: string | null;
  issueKeys: string[];
  discoveredAt: string;
}

export interface RequirementInventoryEntry {
  requirementId: string;
  requirementText: string;
  sourceIssueId: string;
  sourceIssueKey: string;
  sourceRef: string;
  sourceKey: string;
  sourceTitle: string;
  sourceCategory:
    | 'acceptance_criteria'
    | 'description_rule'
    | 'constraint'
    | 'workflow'
    | 'example';
  sourceOrder: number;
  sourcePath?: string | null;
  citations: Citation[];
  coverageStatus: RequirementCoverageStatus;
  caseIndices: number[];
}

export type CoverageSummary =
  | {
      assessment: 'known';
      totalRequirements: number;
      coveredRequirements: number;
      partialRequirements: number;
      uncoveredRequirements: number;
      truncatedByMaxCases: boolean;
    }
  | {
      assessment: 'unknown';
      reason:
        | 'legacy_proposal'
        | 'inventory_unavailable'
        | 'independent_review_unavailable';
      message: string;
    };

export interface CoverageWarning {
  code: 'coverage_truncated_by_max_cases' | 'coverage_incomplete';
  message: string;
  uncoveredRequirementIds: string[];
  suggestedFollowUp: string;
}

export interface DestinationDescriptor {
  provider: PublicationProvider;
  projectId: string;
  projectName: string;
  containerId: string | null;
  containerName: string | null;
  lifecycleState:
    'active' | 'inactive' | 'completed' | 'archived' | 'unknown' | 'stale';
  lifecycleSource:
    'provider_metadata' | 'workspace_config' | 'revalidated_live';
  snapshotAt: string;
}

export interface TraceabilityFieldConfig {
  systemName: string;
  fieldId: number;
  optionIds?: string[];
}

export interface TraceabilityConfig {
  mode: 'custom_fields_first';
  customFieldConfigs: TraceabilityFieldConfig[];
  priorityField?: TraceabilityFieldConfig | null;
  typeField?: TraceabilityFieldConfig | null;
  areaField?: TraceabilityFieldConfig | null;
  fallbackMarker: 'Traceability';
  fallbackFormat: 'preconditions_block' | 'quality_receipt';
  fallbackVersion: 'QUALITY_TRACEABILITY_V1' | 'QUALITY_TRACEABILITY_V2';
}

export interface MetadataMappingSnapshot {
  provider: 'testrail';
  priorityLabels: string[];
  typeLabels: string[];
  areaField:
    | {
        systemName: string;
        mode: 'options';
        optionLabels: string[];
      }
    | {
        systemName: string;
        mode: 'text';
        optionLabels: [];
      }
    | null;
  capturedAt: string;
}

export interface MetadataMappingWarning {
  code: 'metadata_unmapped';
  caseIndex: number;
  field: 'priority' | 'type' | 'area';
  value: string;
  message: string;
}

export interface GenerationMetaSnapshot {
  evidenceReceipt?: { truncatedCount?: number };
  sizingLimit?: { hardCap: number; reached: boolean };
  existingCoverage?: {
    behavior: string;
    existing_case_ids: string[];
    evidence_ids: string[];
    quote: string;
  }[];
  sourceReceipts?: GenerationSourceReceipt[];
  frozenInput?: {
    schemaVersion: 1;
    state: 'collecting' | 'sealed' | 'unavailable';
    knowledgeContext?: {
      enabled: boolean;
      frozen: boolean;
      stop_reason?: string;
      accounting?: {
        deliveredChunks?: number | null;
        deliveryComplete?: boolean;
        logicalRequests: number;
        maxRequests: number;
        chunksPerRequest: number;
        transportAttempts: number;
        receivedChunks: number;
        retainedChunks: number;
        omittedChunks: number;
        complete: boolean;
      };
    };
    executionContext?: {
      workspaceId: string | null;
      credentials: { id: string; label: string }[];
    };
  };

  selectedJiraSource: SelectedJiraSourceSnapshot | null;
  requirementInventory: RequirementInventoryEntry[];
  coverageSummary: CoverageSummary;
  coverageWarning?: CoverageWarning | null;
  destination: DestinationDescriptor;
  traceability: TraceabilityConfig;
  metadataMappings?: MetadataMappingSnapshot | null;
  metadataWarnings?: MetadataMappingWarning[];
  workerSchemaVersion: number;
  maxCases?: number | null;
}

/** One proposed version in a generation run's review loop. `cases` is the raw proposed set; the
 *  feedback fields explain why this version was produced (null on v1). */
export interface RunProposal {
  id: string;
  version: number;
  status: 'proposed' | 'superseded' | 'approved' | 'cancelled';
  refinement_state?: 'available' | 'collecting' | 'unavailable';
  cases: ProposalCase[];
  overall_feedback: string | null;
  per_case_feedback:
    { targetIndex?: number; title?: string; comment: string }[] | null;
  created_testrail_case_ids: string[] | null;
  /** 0-based indices into `cases` that were created in TestRail on approval. Null until approved. */
  created_case_indices: number[] | null;
  /** Frozen story-link discovery metadata; omitted by older servers/proposals. */
  story_linking?: StoryLinkingProposalMeta | null;
  /** Frozen generation evidence, coverage, destination, and traceability snapshot. */
  generationMeta?: GenerationMetaSnapshot | null;
  /** Durable publication rows; omitted/empty on proposals using the legacy publication path. */
  publications?: GeneratedCasePublication[];
  /** Derived publication coverage across all batches for this immutable proposal. */
  publication_state?: 'none' | 'partial' | 'complete';
  /** Cases not yet claimed by any durable publication batch. */
  available_case_indices?: number[];
  /** Immutable destination snapshots for every publication attempt. */
  batches?: GeneratedCasePublicationBatch[];
  created_at: string;
}

export interface CreateGeneratedCasePublicationBatchBody {
  generationVersion?: number;
  previewDigest?: string;
  variableSelection?: GenerationBasketSelection;
  idempotencyKey: string;
  caseIndices: number[];
  destination: GeneratedCasePublicationDestination;
  includeSuggestedStoryLinks?: boolean;
  storyLinksByCase?: { caseIndex: number; refs: string[] }[];
}

export interface CreateGeneratedCasePublicationBatchResponse {
  batch: GeneratedCasePublicationBatch;
  run: Run;
  skipped_existing: string[];
}

/** Body for POST /runs/:id/request-changes — overall note and/or per-case comments. `caseIndices`
 *  (0-based) limits the refine to the selected cases; unselected cases are carried forward unchanged.
 *  Omitted ⇒ revise all. */
export interface RequestChangesBody {
  expectedProposalId?: string;
  expectedProposalVersion?: number;
  overall?: string;
  perCase?: { targetIndex?: number; title?: string; comment: string }[];
  caseIndices?: number[];
}

/** Body for POST /runs/:id/approve — `caseIndices` (0-based) selects which proposed cases to create
 *  in TestRail. Omitted ⇒ create all. */
export interface ApproveBody {
  expectedProposalId?: string;
  expectedProposalVersion?: number;
  caseIndices?: number[];
  /** Reviewer-retained canonical refs per selected case. An explicit [] removes every suggestion. */
  storyLinksByCase?: { caseIndex: number; refs: string[] }[];
}

/** The three shapes a request_user_input() VALUE can take: `credential` (login/password, protected
 *  path), `otp` (one-time code, never persisted), `text` (any other value, opt-in "remember" as an
 *  environment variable). */
export type ValueInputKind = 'credential' | 'otp' | 'text';

/** The full vocabulary a help request can carry (mirrors the service's InputKind): the three value
 *  shapes above, plus `device_takeover` — the agent is stuck on a control it cannot drive, so a human
 *  drives the device instead of typing a value (see DeviceTakeoverRequest). Both shapes ride the same
 *  list (GET /runs/:id/inputs), discriminated by this field. */
export type InputKind = ValueInputKind | 'device_takeover';

/** Lifecycle of one VALUE request. Only `pending` requests are answerable — a settled one has nothing
 *  left to show, and a settled takeover is not served at all (see TakeoverState). */
export type InputRequestState =
  'pending' | 'answered' | 'cancelled' | 'expired';

/** Server-composed identity of what a HITL request wants, replacing worker-authored `prompt` text.
 *  `field` is one of the closed `RequestedField` values on the service; the SPA falls back to a
 *  kind-derived label for an unrecognized field or an absent descriptor (a pre-T3 pending row).
 *  `environment_name` and `case_ref` are set when known; `step_text` is set only for
 *  `field='step_anchor'`, sourced by the service from the case's own recorded step text.
 *  `instruction` is the one part that IS model-authored, set only for `field='action'` (Andrei's
 *  ruling, 2026-08-12): the barrier runs in reverse for an action request — we ask the human to DO
 *  something rather than supply a value, so only the model can say what to do from what is on
 *  screen (e.g. the number to select for Microsoft MFA number-matching). It is never run through
 *  `safeShortText` (which would fail closed on the digit an instruction legitimately contains);
 *  React's own escaping on render is the remaining defense. */
export interface RequestDescriptor {
  field: string;
  environment_name?: string | null;
  case_ref?: string | null;
  step_text?: string | null;
  instruction?: string | null;
}

/** Lifecycle of one device takeover, kept SEPARATE from InputRequestState: 'submitted' and 'blocked'
 *  are meaningless for a value request, and widening a shared enum with them would leak takeover
 *  semantics into every other kind. 'submitted' is the load-bearing intermediate — the human's Submit
 *  ends their turn and decides nothing; only the worker's verified re-read of the device can end the
 *  takeover, which is why it becomes 'answered' (verified) or 'blocked' (not) rather than settling
 *  immediately. */
export type TakeoverState =
  | 'pending'
  | 'submitted'
  | 'answered'
  | 'blocked'
  | 'gave_up'
  | 'expired'
  | 'cancelled';

/** The predicate the worker re-reads the device against, and what the panel renders as the goal. */
export interface TakeoverVerify {
  kind: string;
  column?: string;
  equals?: string;
  labelContains?: string;
  /** Web predicates carry their target in `value` (text, URL fragment, or selector). */
  value?: string;
}

/** The device, as the worker's session actually reports it. `screen` is in device POINTS — the panel
 *  scales a click by it, or every tap lands at ~3x offset on a Retina device with no error anywhere. */
export interface TakeoverDevice {
  model?: string | null;
  osVersion?: string | null;
  screen?: { width: number; height: number } | null;
}

/** What one queued gesture actually did, as reported back by the worker. */
export interface TakeoverCommandResult {
  commandId: string;
  executed: boolean;
  changed: boolean;
  error?: string | null;
  /** Predicate-specific read-back when the worker reported one for this gesture. */
  observed?: string | null;
}

interface HelpRequestCommon {
  request_id: string;
  case_id: string | null;
  key: string;
  /** Server-composed (T3); absent on a pre-T3 row, where the panel falls back to a kind-only label.
   *  It replaces the worker-authored `prompt`, which is no longer on the wire at all. */
  descriptor?: RequestDescriptor;
}

/** One pending request_user_input() call awaiting a value, as listed by GET /runs/:id/inputs. Never
 *  carries the value — the service does not persist or return it (see AnswerInputBody). Carries no
 *  worker-authored `prompt` — the server-composed `descriptor` replaces it. `expires_at` rides only
 *  the help-request list (GET /runs/:id/inputs), which is why it is optional here: the auth-flight
 *  surfaces build this shape without one. */
export interface ValueInputRequest extends HelpRequestCommon {
  kind: ValueInputKind;
  expires_at?: string;
  state: InputRequestState;
}

/** One device takeover, as listed by GET /runs/:id/inputs — the same fields ActiveTakeover carries
 *  (see quality.ts) plus `kind`/`key`/`descriptor`, since this shape rides the shared list rather than the
 *  run-scoped takeover probe. Never carries the session's attach URL or the farm host: has_stream /
 *  stream_token are all the browser learns, and it reaches the device only through the service's
 *  proxied stream route. */
export interface DeviceTakeoverRequest extends HelpRequestCommon {
  kind: 'device_takeover';
  /** Always present on a takeover — the panel counts the hold window down from it. */
  expires_at: string;
  state: TakeoverState;
  target_key: string;
  reason: string;
  expectation: string;
  verify: TakeoverVerify | null;
  device: TakeoverDevice | null;
  /** Web: the page's own CSS-pixel size, and the size of the frames shown. The panel maps a click from
   *  the picture back into the page with them, exactly as `device.screen` does for a phone. Null on a
   *  mobile takeover. */
  viewport?: { width: number; height: number } | null;
  frame?: { width: number; height: number } | null;
  has_stream: boolean;
  stream_token: string | null;
  observed: string | null;
  results: TakeoverCommandResult[];
  pending_commands: number;
  /** Whether the worker verified the declared expectation, or a visible page/device change for a
   *  predicate-less action. Null until the worker settles it. */
  verified: boolean | null;
  /** The takeover's own settle record, distinct from `state`: `state` falls back to the literal
   *  string 'expired' once the live record is gone for ANY reason — including a service restart that
   *  never actually reached the hold window — while this stays null unless the service really settled
   *  it. `state === 'expired' && final_state === null` is exactly a restart orphan; say that to the
   *  human, not a generic timeout. */
  final_state: TakeoverState | null;
}

/** The name the value-request shape had before takeovers joined the same list. Kept as an alias so
 *  every existing caller reads the same type. */
export type PendingInputRequest = ValueInputRequest;

/** Every help request GET /runs/:id/inputs can return, discriminated on `kind`. Before this, a value
 *  request and a takeover had two disjoint wire shapes with no common field to dispatch on — this is
 *  the one type that replaces both. */
export type HelpRequest = ValueInputRequest | DeviceTakeoverRequest;

/** Body for POST /runs/:id/inputs/:requestId — answers a pending VALUE request. `kind` re-classifies
 *  a misclassified value before it's stored (AC9 — flip an agent-declared `text` to `credential` when
 *  the field is actually a password); omit to keep the agent-declared kind. `remember` is the opt-in
 *  for `kind=text` persistence as an environment variable (AC7) — meaningless for `credential`
 *  (always stored) or `otp` (never stored). Never `device_takeover`: a takeover is never answered with
 *  a value (the service refuses it, ConflictException) — the human drives the device instead. */
export interface AnswerInputBody {
  value: string;
  kind?: ValueInputKind;
  remember?: boolean;
}

/** Lifecycle of one environment-scoped authentication flight. `account_mismatch` is a linked
 * exception flight inside the same environment group; it never exposes either account identity. */
export type AuthFlightState =
  | 'pending'
  | 'applying'
  | 'answered'
  | 'expired'
  | 'reset'
  | 'account_mismatch';

/** Safe, normalized viewer contract for GET /runs/:id/auth-flights. This intentionally contains no
 * worker-authored prompt/key, case details, credential values, cookies, or browser storage state. */
export interface AuthFlight {
  flight_id: string;
  run_id: string;
  environment_id: string | null;
  /** Secret-free selector for an environmentless flight. Never an account alias. */
  environment_alias: string | null;
  /** Opaque server-issued grouping/reset handle; never rendered. */
  environment_reset_key: string | null;
  environment_name: string;
  kind: 'auth';
  reason_code: string;
  state: AuthFlightState;
  waiting_case_count: number;
  expires_at: string | null;
  account_mismatch: boolean;
  safe_label: string;
  /** Server-composed identity of the wanted field, replacing the client-synthesized `safe_label`
   *  wherever present (a pre-T3 flight row has none, and the SPA falls back to `safe_label`). */
  descriptor?: RequestDescriptor;
  parent_flight_id: string | null;
  is_exception: boolean;
}

/** Secret-bearing write body. The response only returns state and never echoes `value`. */
export interface AnswerAuthFlightBody {
  value: string;
}

/** Reset is scoped to one selected environment. The opaque server-issued key is preferred for an
 * environmentless flight so two logical identities can never be reset as one null bucket. */
export interface ResetAuthFlightBody {
  environmentId: string | null;
  environmentResetKey?: string;
  environmentAlias?: string;
}

/** A potential bug observed during a bug-hunt browse (display-only). `screenshot`, when present, is
 *  an artifact key under the run (renderable via artifactUrl). */
export interface Finding {
  title: string;
  severity: 'low' | 'medium' | 'high';
  area?: string;
  observation: string;
  screenshot?: string;
  evidence_status?: 'captured' | 'unavailable';
}

// One relabeled low-level action inside an execution-log phase ("Opened https://…", "Clicked Sign in")
// with the raw tool name kept for the icon / detail view.
export interface ExecutionStepDetail {
  text: string;
  tool?: string;
}

// One phase of a browse's execution log (display-only): a descriptive title (the agent's narration, or
// a synthesized fallback) and the relabeled browser actions taken during that phase, shown on expand.
// This is the current wire shape produced by the service/worker for explore runs.
export interface ExecutionPhase {
  title: string;
  steps?: ExecutionStepDetail[];
  /** Offset (ms) of this phase into the session recording. When present (and the run has a video),
   *  the report shows a "jump to this moment" button that seeks the inline recording. */
  timecode_ms?: number;
}

// The flat note/action shape produced before grouping existed. Still emitted by no current code, but
// runs persisted at rest before the grouped shape can carry it — the renderer tolerates it.
/** @deprecated legacy flat execution-log entry — older persisted runs only. */
export interface LegacyExecutionStep {
  type: 'note' | 'action';
  text: string;
  tool?: string;
}

// An execution-log entry is either a grouped phase (current) or a legacy flat entry (older runs).
export type ExecutionStep = ExecutionPhase | LegacyExecutionStep;

// ── Generation transparency (GET /runs/:id/generation-context + seed artifacts) ──

/** Generation mode on the source manifest / context versions. */
export type GenerationContextMode = 'synthesize' | 'refine';

/** One uploaded document on the generation source manifest. */
export interface GenerationContextDocument {
  key: string;
  filename: string;
  kind: string;
}

/** One context-source snapshot on the generation source manifest. */
export interface GenerationContextSource {
  key: string;
  filename: string;
  kind: string;
  label?: string;
  /** False (or absent on a legacy manifest, where it always meant true) once selected but never
   *  fetched — the source has an empty key/filename and nothing to link to. */
  staged?: boolean;
}

/** The output destination as typed at launch — provider plus whichever raw id fields it uses. */
export interface GenerationContextDestination {
  provider: 'testrail' | 'zephyr' | 'azure' | 'xray' | 'own-tms';
  project_id: string;
  section_id: number | null;
  folder_id: string | null;
  plan_id: string | null;
  suite_id: string | null;
  folder_path: string | null;
  test_set_id: string | null;
  test_plan_id: string | null;
}

/** One existing-tests snapshot on the generation source manifest. */
export interface GenerationContextExistingTest {
  key: string;
  filename: string;
}

/** Live-app hint on the source manifest — never carries a password. */
export interface GenerationContextLiveApp {
  start_url: string | null;
  credential_login_as: string | null;
}

/**
 * Latest generation source manifest from GET generation-context (`manifest`).
 * Snake_case wire; fields may be absent so UI sections stay quiet.
 */
export interface GenerationContextManifest {
  instructions?: string;
  scenario_intent?: 'standalone' | 'e2e' | 'uat';
  max_cases?: number | null;
  platform?: Platform | string;
  claude_model?: string;
  mode?: GenerationContextMode | string;
  version?: number;
  live_app?: GenerationContextLiveApp | null;
  documents?: GenerationContextDocument[];
  context_sources?: GenerationContextSource[];
  existing_tests?: GenerationContextExistingTest[];
  destination?: GenerationContextDestination;
}

/** One context source the run actually read, frozen at launch — the compiled query included. */
export interface ResolvedContextScopeEntry {
  kind: string;
  origin: 'prompt' | 'query';
  query: string;
  label: string;
  site: string | null;
  instance: string | null;
  max_items: number | null;
  include_comments: boolean;
}

/** What one prompt was understood to mean, including the parts of it that went unused. */
export interface ResolvedContextScopePrompt {
  kind: 'jira' | 'confluence';
  prompt: string;
  warnings: string[];
  unresolved: string[];
}

export interface ResolvedContextScopeSnapshot {
  resolved_at: string;
  entries: ResolvedContextScopeEntry[];
  prompts: ResolvedContextScopePrompt[];
}

/** Relative artifact keys for one version's seed prompt. */
export interface GenerationContextSeedPrompt {
  structured_key: string;
  rendered_key?: string | null;
}

/** One version stub on GET generation-context. */
export interface GenerationContextVersion {
  version: number;
  mode: GenerationContextMode | string;
  seed_prompt: GenerationContextSeedPrompt | null;
  trace_key?: string;
  trace_ref?: {
    state:
      | 'indexed'
      | 'missing_manifest'
      | 'ambiguous'
      | 'version_identity_unavailable';
    key: string | null;
    identity: {
      owner_id: string;
      case_execution_id: string;
      attempt: number;
      generation_version: number;
    } | null;
  };
}

/** One staged input descriptor on GET generation-context. */
export interface GenerationContextInput {
  key: string;
  filename: string;
  kind: string;
  content_type?: string;
}

/** Snake_case response for GET /v1/quality/runs/:id/generation-context. */
export interface GenerationContextResponse {
  generation_plan?: import('../../features/quality/GenerationComposer.js').GenerationPlan;
  source_controls?: GenerationSourceControls;
  authoring_environment_name?: string | null;
  exploration?: GenerationExplorationSelection;
  schema_version?: 2;
  projection?: 'browser_v1';
  run_id: string;
  run_kind: string;
  manifest: GenerationContextManifest | null;
  versions: GenerationContextVersion[];
  inputs: GenerationContextInput[];
  /** The per-source compiled query (WIQL/JQL/CQL included), from the run's latest proposal. */
  resolved_context_scope?: ResolvedContextScopeSnapshot | null;
}

/** Basename↔artifact mapping inside the structured seed-prompt JSON. */
export interface StructuredSeedRef {
  artifact_key: string;
  prompt_path_basename: string;
}

/** Login block on the structured seed — `password` is always null on the wire. */
export interface StructuredSeedLogin {
  required: boolean;
  login_as: string | null;
  password_present: boolean;
  password: null;
}

/** Live-app block on the structured seed (URL already redacted by the worker). */
export interface StructuredSeedLiveApp {
  start_url_redacted: string;
}

/** Refine-mode revision block on the structured seed. */
export interface StructuredSeedRevision {
  feedback: unknown;
  revise_indices: number[];
}

/**
 * Structured seed-prompt JSON from `seed_prompt.structured_key`
 * (`generation/attempt-0/seed-prompt.v{N}.json`). Never display `login.password`.
 */
export interface StructuredSeedPrompt {
  schema_version: number;
  version: number;
  mode: string;
  template: string;
  instructions: string;
  max_cases_clause: string;
  login: StructuredSeedLogin;
  document_refs: StructuredSeedRef[];
  existing_tests_refs: StructuredSeedRef[];
  live_app: StructuredSeedLiveApp | null;
  revision: StructuredSeedRevision | null;
  rendered_redacted_key?: string | null;
}

// ── Auto-Recorded Test Replay findings (cti-wrapper/docs/MEDA-416 AutoRecordedTestReplay.md) ──

export type FindingType =
  | 'heal'
  | 'regression_evidence'
  | 'merge_suggestion'
  | 'step_created'
  | 'step_forked'
  | 'demotion'
  | 'quarantine'
  | 'tms_change'
  | 'suspicious_pass'
  | 'report_integrity'
  | 'cluster'
  | 'coverage_gap'
  | 'poorly_described_case'
  | 'non_cacheable';

export type FindingSeverity = 'info' | 'warn' | 'critical';
export type FindingReviewStatus = 'unreviewed' | 'reviewed' | 'actioned';

/**
 * A replay finding, served snake_case. `payload` shape varies by `type` — render it defensively.
 * Named `ReplayFinding` to avoid colliding with the exploration-report `Finding` (a bug in a run).
 */
export interface ReplayFinding {
  id: string;
  project_id: string;
  run_id: string | null;
  type: FindingType;
  severity: FindingSeverity;
  fingerprint: string | null;
  payload: Record<string, unknown>;
  review_status: FindingReviewStatus;
  linked_finding_id: string | null;
  created_at: string;
  updated_at: string;
}

export interface QualitySkillPreview {
  case_id: string;
  phase: 'step' | 'precondition';
  index: number;
  id: string;
  trigger: string;
  title: string;
  scope: string;
  digest: string;
}

export interface QualitySkillExecution {
  version: 1;
  invocations: {
    activation_id: string;
    skill_id: string;
    trigger?: string;
    title?: string;
    scope?: string;
    digest: string;
    phase: 'step' | 'precondition';
    index: number;
    status: string;
    outputs: Record<string, unknown>;
    actions: {
      action: string;
      status: string;
      observation?: string;
      expected_text?: string;
      actual_text?: string;
      receipts: { completed: boolean }[];
    }[];
  }[];
}
